import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import { Line } from "react-chartjs-2";
import { API } from "../lib/config";
import { fastGetAsync } from "../lib/fastFetch";
import { isEquityOpen } from "../lib/marketClock";
import { openSSE } from "../lib/sse";
import { fmt$, fmtN, parseOCC, ACTION_COLORS } from "../lib/format";
import { EmailAlertsRow, EnableBrowserNotifButton } from "./NotifRows";
import OmegaEnhancementsCard from "./OmegaEnhancementsCard";
import QuickTradeModal from "./QuickTradeModal";
import TrailStopModal from "./TrailStopModal";
import PositionDetailModal from "./PositionDetailModal";

function OpenPositionModal({ position, onClose }) {
  const isOption = String(position.type || "").toUpperCase().includes("OPTION");
  const isCrypto = String(position.type || "").toUpperCase().includes("CRYPTO");

  // Parse OCC symbol for options (e.g. ADA251219C00001500 → not really OCC, but handle standard OCC)
  const parseOCCLocal = sym => {
    const m = /^([A-Z]+)(\d{2})(\d{2})(\d{2})([CP])(\d{8})$/.exec(sym || "");
    if (!m) return null;
    const [, tk, yy, mm, dd, cp, rawStrike] = m;
    return { ticker: tk, strike: parseInt(rawStrike) / 1000, side: cp === "C" ? "Call" : "Put", expDate: `${parseInt(mm)}/${parseInt(dd)}/${yy}` };
  };
  const occ = isOption ? parseOCCLocal(position.symbol) : null;
  const ticker = occ?.ticker || position.ticker || position.symbol || "";
  const entryPrice = position.entryPrice || position.averageCost || position.avgCost || position.costBasis || 0;
  const qty = position.quantity || 1;

  const dayPnl = position.dayGainValue ?? position.dayPnl ?? 0;
  const dayPct = position.dayGainPct ?? 0;
  const totalPct = position.gainPct ?? position.pnlPct ?? 0;
  const isUp = dayPnl >= 0;

  const fmtN = (v, d = 2) => v == null ? "—" : Number(v).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
  const fmt$ = v => v == null ? "—" : `$${fmtN(Math.abs(v), 2)}`;

  const typeLabel = isOption ? (occ?.side === "Put" || position.side === "Put" || position.optionType === "put" ? "PUT" : "CALL") : isCrypto ? "CRY" : "STK";
  const typeColor = isOption ? "#a78bfa" : isCrypto ? "#f59e0b" : "#60a5fa";

  // Enriched watch data from /omega/watch
  const [watchPos, setWatchPos] = useState(null);

  // Mini live chart (same approach as mobile LivePositionCard)
  const [miniChart, setMiniChart] = useState(null);

  // Live SSE price tick
  const [liveTick, setLiveTick] = useState(null);
  const forecastChartRef = useRef(null);
  const forecastDataRef = useRef(null);

  // P&L forecast data
  const [forecast, setForecast] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(true);

  // Omega Watch enriched data + TEA
  const [tea, setTea] = useState(null);

  // ── Fetch omega/watch for enriched position data ──
  useEffect(() => {
    fastGetAsync(`${API}/omega/watch`)
      .then(r => {
        const match = (r.data?.positions || []).find(p => p.symbol === position.symbol || p.ticker === ticker);
        if (match) setWatchPos(match);
      }).catch(() => {});
    // TEA score
    if (ticker) axios.get(`${API}/tea/score?symbol=${encodeURIComponent(ticker)}`).then(r => setTea(r.data)).catch(() => {});
  }, [ticker]);

  // ── Live mini chart from /ticks/history (same as mobile LivePositionCard) ──
  useEffect(() => {
    if (!ticker) return;
    const load = () => {
      const chartSym = isCrypto ? `${ticker}-USD` : ticker;
      axios.get(`${API}/ticks/history?symbol=${encodeURIComponent(position.symbol)}`)
        .then(r => {
          const bars = r.data?.bars || [];
          if (bars.length > 1) {
            const closes = bars.map(b => b.c);
            const first = closes[0], last = closes[closes.length - 1];
            setMiniChart({
              labels: bars.map(b => new Date(b.t * (b.t < 1e10 ? 1000 : 1)).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })),
              datasets: [{ data: closes, borderColor: last >= first ? "#4ade80" : "#f87171", backgroundColor: last >= first ? "rgba(74,222,128,0.08)" : "rgba(248,113,113,0.08)", fill: true, pointRadius: 0, borderWidth: 1.5, tension: 0.2 }],
            });
          }
        }).catch(() => {
          // Fallback to underlying ticker for options
          if (occ && chartSym !== position.symbol) {
            axios.get(`${API}/ticks/history?symbol=${encodeURIComponent(occ.ticker)}`).then(r2 => {
              const bars2 = r2.data?.bars || [];
              if (bars2.length > 1) {
                const closes = bars2.map(b => b.c);
                const first = closes[0], last = closes[closes.length - 1];
                setMiniChart({
                  labels: bars2.map(b => new Date(b.t * (b.t < 1e10 ? 1000 : 1)).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })),
                  datasets: [{ data: closes, borderColor: last >= first ? "#4ade80" : "#f87171", backgroundColor: last >= first ? "rgba(74,222,128,0.08)" : "rgba(248,113,113,0.08)", fill: true, pointRadius: 0, borderWidth: 1.5, tension: 0.2 }],
                });
              }
            }).catch(() => {});
          }
        });
    };
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [ticker]);

  // ── SSE live tick ──
  useEffect(() => {
    if (!ticker) return;
    // openSSE: silent-stream watchdog + reconnect (this had no onerror at all).
    const conn = openSSE(`${API}/stream/position?symbol=${encodeURIComponent(ticker)}`, { tick: (t) => {
      try {
        if (!t || typeof t !== "object") return;
        setLiveTick(prev => {
          // First tick ever — if forecast is still empty, retry with live price as entry
          if (!prev && t.price > 0 && !forecastDataRef.current) {
            buildForecast(entryPrice || t.price);
          }
          return t;
        });
        // Update last actual P&L point in forecast chart
        if (forecastDataRef.current && forecastChartRef.current && t.price > 0) {
          const histLen = forecastDataRef.current._histLen || 0;
          if (histLen > 0) {
            const ep = entryPrice || t.price;
            const delta = watchPos?.greeks?.delta || position.greeks?.delta || 0.5;
            // _openPrice is the stock's open price returned by backend (not option premium)
            const stockOpen = forecastDataRef.current._openPrice || t.price;
            const pnlVal = isOption
              ? delta * (t.price - stockOpen) * 100 * qty
              : (t.price - ep) * qty;
            forecastDataRef.current.datasets[0].data[histLen - 1] = pnlVal;
            forecastChartRef.current.update("none");
          }
        }
      } catch {}
    } });
    return () => conn.close();
  }, [ticker]);

  // ── P&L Forecast from /position/pnl-forecast ──
  const buildForecast = useCallback((ep) => {
    if (!ticker) return;
    setForecastLoading(true);
    const delta = watchPos?.greeks?.delta || position.greeks?.delta || 0.5;
    axios.post(`${API}/position/pnl-forecast`, { symbol: ticker, entryPrice: ep, quantity: qty, type: position.type || "EQUITY", delta })
      .then(r => {
        const d = r.data;
        // If backend returned no data at all, nothing to render
        if (!d.labels?.length && !d.fcLabels?.length) return;
        const histLen = d.labels?.length || 0;
        const fcLen = d.fcLabels?.length || 0;
        const allLabels = [...(d.labels || []), ...(d.fcLabels || [])];
        const actualData = [...(d.history || []), ...Array(fcLen).fill(null)];
        const connPnl = histLen > 0 ? d.history[histLen - 1] : 0;
        const fcData = [...Array(Math.max(0, histLen - 1)).fill(null), connPnl, ...(d.forecast || [])];
        const fcUpper = [...Array(Math.max(0, histLen - 1)).fill(null), connPnl, ...(d.fcUpper || [])];
        const fcLower = [...Array(Math.max(0, histLen - 1)).fill(null), connPnl, ...(d.fcLower || [])];
        const chartObj = {
          labels: allLabels, _histLen: histLen, _openPrice: d.openPrice ?? ep ?? 0,
          datasets: [
            { label: "P&L", data: actualData, borderColor: isUp ? "#22c55e" : "#f87171", backgroundColor: isUp ? "rgba(34,197,94,0.08)" : "rgba(248,113,113,0.08)", fill: true, pointRadius: 0, borderWidth: 2, tension: 0.3 },
            { label: "Zi Forecast", data: fcData, borderColor: "#a78bfa", borderDash: [5, 4], backgroundColor: "transparent", fill: false, pointRadius: 0, borderWidth: 1.5, tension: 0.3 },
            { label: "Upper", data: fcUpper, borderColor: "rgba(167,139,250,0.2)", backgroundColor: "rgba(167,139,250,0.05)", fill: "+1", pointRadius: 0, borderWidth: 0, tension: 0.3 },
            { label: "Lower", data: fcLower, borderColor: "rgba(167,139,250,0.2)", backgroundColor: "transparent", fill: false, pointRadius: 0, borderWidth: 0, tension: 0.3 },
          ],
          _meta: d,
        };
        forecastDataRef.current = chartObj;
        setForecast(d);
      }).catch(() => {})
      .finally(() => setForecastLoading(false));
  }, [ticker, qty, isUp]);

  useEffect(() => { buildForecast(entryPrice); }, [ticker]);

  // Use enriched watch data where available
  const w = watchPos || {};
  const greeks = w.greeks || position.greeks || {};
  const signals = w.signals || [];
  const actionStr = w.action || "HOLD";
  const actionColor = ACTION_COLORS[actionStr] || "#60a5fa";
  const teaColor = t => !t ? "var(--muted)" : t >= 70 ? "#4ade80" : t >= 40 ? "#fbbf24" : "#f87171";

  const insight = signals.length > 0 ? signals[0] : (() => {
    const gp = totalPct, dp = dayPct;
    if (dp > 30) return `Surging +${fmtN(dp, 1)}% today — strong momentum. Consider scaling out to lock gains.`;
    if (dp > 15) return `Up +${fmtN(dp, 1)}% today — bullish momentum building.`;
    if (dp < -25) return `Sharp decline ${fmtN(dp, 1)}% today — evaluate exit.`;
    if (dp < -10) return `Weak session (${fmtN(dp, 1)}% today). Omega Risk Gate monitoring.`;
    if (gp > 30) return "Strong unrealized gains — consider scaling out 50% to lock profits.";
    if (gp > 10) return "Position in profit. Hold with trailing stop at entry.";
    if (gp > -5) return "Position near breakeven — current regime determines validity.";
    return `Total P&L ${fmtN(gp, 1)}%. In drawdown — monitor for reversal signals.`;
  })();

  const miniOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { enabled: false } }, scales: { x: { display: false }, y: { display: false } } };
  const forecastOpts = {
    responsive: true, maintainAspectRatio: false, animation: false,
    plugins: { legend: { display: true, labels: { color: "var(--muted)", boxWidth: 8, font: { size: 9 }, filter: i => !["Upper","Lower"].includes(i.text) } }, tooltip: { enabled: false } },
    scales: {
      x: { ticks: { color: "var(--dim)", font: { size: 8 }, maxTicksLimit: 8 }, grid: { color: "var(--grid)" } },
      y: { ticks: { color: "var(--dim)", font: { size: 9 }, callback: v => v >= 0 ? `+$${Math.abs(v).toFixed(0)}` : `-$${Math.abs(v).toFixed(0)}` }, grid: { color: "var(--grid)" } },
    },
  };
  const slope = forecast?.slope || 0;
  const projectedEOD = forecast ? (forecast.lastPnl + slope * Math.max(0, (960 - (new Date().getHours() * 60 + new Date().getMinutes())))) : null;
  const dte = w.dte ?? position.dte;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: 24, border: "1px solid var(--border)", width: "100%", maxWidth: 580, maxHeight: "92vh", overflowY: "auto", boxShadow: "0 24px 80px rgba(0,0,0,0.22)" }}>

        {/* ── Header ── */}
        <div style={{ padding: "20px 24px 0", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 14, background: `${typeColor}15`, border: `1px solid ${typeColor}30`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
              <span style={{ fontSize: 9, fontWeight: 900, color: typeColor, letterSpacing: "0.05em" }}>{typeLabel}</span>
            </div>
            <div>
              <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 3 }}>Open Position</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 24, fontWeight: 900, letterSpacing: "-0.03em", color: "var(--text)", lineHeight: 1 }}>{ticker}</span>
                {w.itm != null && <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 6, background: w.itm ? "rgba(74,222,128,0.12)" : "rgba(248,113,113,0.12)", color: w.itm ? "#4ade80" : "#f87171", border: `1px solid ${w.itm ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}` }}>{w.itm ? "ITM" : "OTM"}</span>}
                {dte != null && <span style={{ fontSize: 9, fontWeight: 700, color: dte <= 7 ? "#f87171" : dte <= 21 ? "#fbbf24" : "var(--muted)" }}>{dte}d</span>}
                {tea?.teaScore != null && <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 6, background: `${teaColor(tea.teaScore)}15`, color: teaColor(tea.teaScore), border: `1px solid ${teaColor(tea.teaScore)}35` }}>TEA {tea.teaScore}</span>}
              </div>
              {isOption && <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 3 }}>
                {w.side || occ?.side || position.side} · Strike ${fmtN(w.strike || occ?.strike || position.strike, 2)} · Exp {w.expDate || occ?.expDate || position.expiry}
              </div>}
              {!isOption && position.name && <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{position.name}</div>}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, flexShrink: 0 }}>
            <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: "50%", border: "1px solid var(--border)", background: "var(--layer1)", color: "var(--muted)", fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
            <div style={{ padding: "3px 10px", borderRadius: 8, fontSize: 10, fontWeight: 800, background: `${actionColor}15`, color: actionColor, border: `1px solid ${actionColor}30` }}>{actionStr}</div>
          </div>
        </div>

        {/* ── Live Tick + Mini Chart ── */}
        <div style={{ margin: "14px 24px 0", display: "grid", gridTemplateColumns: miniChart ? "1fr 140px" : "1fr", gap: 10, alignItems: "stretch" }}>
          <div style={{ padding: "10px 14px", borderRadius: 14, background: "var(--layer1)", border: "1px solid var(--border)" }}>
            {liveTick && liveTick.price > 0 ? (
              <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 9, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 1 }}>Live Price</div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: "var(--text)" }}>${fmtN(liveTick.price, liveTick.price < 5 ? 4 : 2)}</div>
                </div>
                <div style={{ width: 1, height: 30, background: "var(--border)", flexShrink: 0 }} />
                <div><div style={{ fontSize: 8, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", marginBottom: 1 }}>Bid</div><div style={{ fontSize: 12, fontWeight: 700, color: "var(--dim)" }}>${fmtN(liveTick.bid, 4)}</div></div>
                <div><div style={{ fontSize: 8, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", marginBottom: 1 }}>Ask</div><div style={{ fontSize: 12, fontWeight: 700, color: "var(--dim)" }}>${fmtN(liveTick.ask, 4)}</div></div>
                {liveTick.volume > 0 && <div><div style={{ fontSize: 8, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", marginBottom: 1 }}>Vol</div><div style={{ fontSize: 12, fontWeight: 700, color: "var(--dim)" }}>{Number(liveTick.volume).toLocaleString()}</div></div>}
                <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4, fontSize: 9, fontWeight: 700, color: "#22c55e" }}><span style={{ width: 5, height: 5, borderRadius: "50%", background: "#22c55e", display: "inline-block", animation: "pulse 1s infinite" }} />LIVE</span>
              </div>
            ) : (
              <div style={{ fontSize: 11, color: "var(--dim)", display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 5, height: 5, borderRadius: "50%", background: "#22c55e", animation: "pulse 1s infinite", display: "inline-block" }} />Connecting to live feed…</div>
            )}
          </div>
          {miniChart && (
            <div style={{ height: 64, borderRadius: 14, overflow: "hidden", background: "var(--layer1)", border: "1px solid var(--border)", padding: "6px" }}>
              <Line data={miniChart} options={miniOpts} />
            </div>
          )}
        </div>

        {/* ── P&L Forecast Chart ── */}
        <div style={{ margin: "14px 24px 0" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted)" }}>Live P&L · Zi Forecast</div>
            {projectedEOD != null && (
              <div style={{ fontSize: 11, fontWeight: 700, color: projectedEOD >= 0 ? "var(--green)" : "var(--red)", background: projectedEOD >= 0 ? "rgba(34,197,94,0.08)" : "rgba(248,113,113,0.08)", border: `1px solid ${projectedEOD >= 0 ? "rgba(34,197,94,0.2)" : "rgba(248,113,113,0.2)"}`, borderRadius: 8, padding: "3px 9px" }}>
                EOD est. {projectedEOD >= 0 ? "+" : "−"}${fmtN(Math.abs(projectedEOD), 2)}
              </div>
            )}
          </div>
          <div style={{ height: 180, borderRadius: 16, overflow: "hidden", background: "var(--layer1)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", padding: forecastLoading || (!forecastDataRef.current && !miniChart) ? 0 : "8px 12px 4px" }}>
            {forecastLoading
              ? <span style={{ fontSize: 11, color: "var(--dim)", display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: "#a78bfa", display: "inline-block", animation: "pulse 1s infinite" }} />Building P&L forecast…</span>
              : forecastDataRef.current
                ? <Line ref={forecastChartRef} data={forecastDataRef.current} options={forecastOpts} style={{ width: "100%", height: "100%" }} />
                : miniChart
                  ? <Line data={miniChart} options={{ ...miniOpts, scales: { x: { ticks: { color: "var(--dim)", font: { size: 8 }, maxTicksLimit: 8 }, grid: { color: "var(--grid)" } }, y: { ticks: { color: "var(--dim)", font: { size: 9 } }, grid: { color: "var(--grid)" } } }, plugins: { legend: { display: false }, tooltip: { enabled: false } } }} style={{ width: "100%", height: "100%" }} />
                  : <span style={{ fontSize: 11, color: "var(--dim)", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--dim)", animation: "pulse 1s infinite", display: "inline-block" }} />Fetching price data…</span>}
          </div>
          {forecast && (
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              {[
                { label: "Target", val: `+$${fmtN(forecast.profitTargetPnl, 2)}`, bg: "rgba(34,197,94,0.06)", bd: "rgba(34,197,94,0.2)", c: "var(--green)" },
                { label: "Stop", val: `−$${fmtN(Math.abs(forecast.stopLossPnl), 2)}`, bg: "rgba(248,113,113,0.06)", bd: "rgba(248,113,113,0.2)", c: "var(--red)" },
                { label: "Trend", val: `${slope > 0 ? "▲" : slope < 0 ? "▼" : "—"} $${fmtN(Math.abs(slope), 2)}/bar`, bg: "var(--layer1)", bd: "var(--border)", c: slope > 0 ? "var(--green)" : slope < 0 ? "var(--red)" : "var(--muted)" },
              ].map(({ label, val, bg, bd, c }) => (
                <div key={label} style={{ flex: 1, padding: "6px 10px", borderRadius: 10, background: bg, border: `1px solid ${bd}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 9, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase" }}>{label}</span>
                  <span style={{ fontSize: 11, fontWeight: 800, color: c }}>{val}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Key Metrics ── */}
        <div style={{ padding: "14px 24px 0", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
          {[
            { label: "Day P&L", val: `${dayPnl >= 0 ? "+" : "−"}$${fmtN(Math.abs(dayPnl), 2)}`, color: isUp ? "var(--green)" : "var(--red)" },
            { label: "Day %", val: `${dayPct >= 0 ? "+" : ""}${fmtN(dayPct, 2)}%`, color: dayPct >= 0 ? "var(--green)" : "var(--red)" },
            { label: "Total %", val: `${totalPct >= 0 ? "+" : ""}${fmtN(totalPct, 1)}%`, color: totalPct >= 0 ? "var(--green)" : "var(--red)" },
            { label: "Value", val: fmt$(w.currentValue || position.currentValue || position.positionValue), color: "var(--text)" },
          ].map(({ label, val, color }) => (
            <div key={label} style={{ padding: "9px 10px", borderRadius: 12, background: "var(--layer1)", border: "1px solid var(--border)", textAlign: "center" }}>
              <div style={{ fontSize: 8, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 3 }}>{label}</div>
              <div style={{ fontSize: 13, fontWeight: 800, color }}>{val}</div>
            </div>
          ))}
        </div>

        {/* ── Option Details (from /omega/watch) ── */}
        {isOption && (
          <div style={{ padding: "12px 24px 0" }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 8 }}>Option Details</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
              {w.stockPrice > 0 && <div style={{ padding: "8px 10px", borderRadius: 10, background: "var(--layer1)", border: "1px solid var(--border)" }}><div style={{ fontSize: 8, color: "var(--muted)", fontWeight: 700, marginBottom: 2 }}>Stock Price</div><div style={{ fontSize: 12, fontWeight: 800, color: "var(--text)" }}>${fmtN(w.stockPrice, 2)}</div></div>}
              {(w.optionBid != null || w.optionAsk != null) && <div style={{ padding: "8px 10px", borderRadius: 10, background: "var(--layer1)", border: "1px solid var(--border)" }}><div style={{ fontSize: 8, color: "var(--muted)", fontWeight: 700, marginBottom: 2 }}>Bid / Ask</div><div style={{ fontSize: 12, fontWeight: 800, color: "var(--text)" }}>${fmtN(w.optionBid, 2)} / ${fmtN(w.optionAsk, 2)}</div></div>}
              {w.moneyness != null && <div style={{ padding: "8px 10px", borderRadius: 10, background: "var(--layer1)", border: "1px solid var(--border)" }}><div style={{ fontSize: 8, color: "var(--muted)", fontWeight: 700, marginBottom: 2 }}>Moneyness</div><div style={{ fontSize: 12, fontWeight: 800, color: w.itm ? "#4ade80" : "#f87171" }}>{w.itm ? "+" : "−"}{Math.abs(w.moneyness)}%</div></div>}
              {w.breakeven != null && <div style={{ padding: "8px 10px", borderRadius: 10, background: "var(--layer1)", border: "1px solid var(--border)" }}><div style={{ fontSize: 8, color: "var(--muted)", fontWeight: 700, marginBottom: 2 }}>Breakeven</div><div style={{ fontSize: 12, fontWeight: 800, color: "var(--text)" }}>${fmtN(w.breakeven, 2)}</div>{w.distToBreakeven != null && <div style={{ fontSize: 9, color: "var(--muted)", marginTop: 1 }}>+{fmtN(w.distToBreakeven, 1)}% away</div>}</div>}
              {w.optionVolume > 0 && <div style={{ padding: "8px 10px", borderRadius: 10, background: "var(--layer1)", border: "1px solid var(--border)" }}><div style={{ fontSize: 8, color: "var(--muted)", fontWeight: 700, marginBottom: 2 }}>Opt Volume</div><div style={{ fontSize: 12, fontWeight: 800, color: "var(--text)" }}>{Number(w.optionVolume).toLocaleString()}</div></div>}
              {w.avgCost != null && <div style={{ padding: "8px 10px", borderRadius: 10, background: "var(--layer1)", border: "1px solid var(--border)" }}><div style={{ fontSize: 8, color: "var(--muted)", fontWeight: 700, marginBottom: 2 }}>Avg Cost</div><div style={{ fontSize: 12, fontWeight: 800, color: "var(--text)" }}>${fmtN(w.avgCost, 4)}</div></div>}
            </div>
          </div>
        )}

        {/* ── Cost Basis row (all types) ── */}
        <div style={{ padding: "10px 24px 0", display: "flex", gap: 14, flexWrap: "wrap", fontSize: 10, color: "var(--muted)" }}>
          <span>Qty: <strong style={{ color: "var(--text)" }}>{isOption ? `${qty} contract${qty !== 1 ? "s" : ""}` : fmtN(qty, qty % 1 === 0 ? 0 : 2)}</strong></span>
          {entryPrice > 0 && <span>Entry: <strong style={{ color: "var(--text)" }}>${fmtN(entryPrice, entryPrice < 1 ? 4 : 2)}</strong></span>}
          {(w.avgCost || position.avgCost) != null && !isOption && <span>Avg Cost: <strong style={{ color: "var(--text)" }}>${fmtN(w.avgCost || position.avgCost, 4)}</strong></span>}
          {(w.totalCost || position.totalCost) != null && <span>Cost Basis: <strong style={{ color: "var(--text)" }}>{fmt$(w.totalCost || position.totalCost)}</strong></span>}
          {position.pctOfPortfolio != null && <span>% of Portfolio: <strong style={{ color: "var(--text)" }}>{fmtN(position.pctOfPortfolio, 1)}%</strong></span>}
          {position.lastPrice != null && <span>Last: <strong style={{ color: "var(--text)" }}>${fmtN(position.lastPrice, position.lastPrice < 1 ? 4 : 2)}</strong></span>}
        </div>

        {/* ── Greeks (options) ── */}
        {isOption && Object.keys(greeks).length > 0 && (
          <div style={{ padding: "12px 24px 0" }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 8 }}>Greeks</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {greeks.delta != null && <div style={{ padding: "6px 10px", borderRadius: 10, background: "var(--layer1)", border: "1px solid var(--border)" }}><div style={{ fontSize: 8, color: "var(--muted)", fontWeight: 700, marginBottom: 2 }}>Delta</div><div style={{ fontSize: 12, fontWeight: 800, color: "#60a5fa" }}>{fmtN(greeks.delta, 3)}</div></div>}
              {greeks.gamma != null && <div style={{ padding: "6px 10px", borderRadius: 10, background: "var(--layer1)", border: "1px solid var(--border)" }}><div style={{ fontSize: 8, color: "var(--muted)", fontWeight: 700, marginBottom: 2 }}>Gamma</div><div style={{ fontSize: 12, fontWeight: 800, color: "var(--text)" }}>{fmtN(greeks.gamma, 4)}</div></div>}
              {greeks.theta != null && <div style={{ padding: "6px 10px", borderRadius: 10, background: "var(--layer1)", border: "1px solid var(--border)" }}><div style={{ fontSize: 8, color: "var(--muted)", fontWeight: 700, marginBottom: 2 }}>Theta</div><div style={{ fontSize: 12, fontWeight: 800, color: "#f87171" }}>{fmtN(greeks.theta, 4)}</div><div style={{ fontSize: 8, color: "var(--dim)" }}>${fmtN(Math.abs(greeks.theta || 0) * 100, 2)}/day</div></div>}
              {greeks.vega != null && <div style={{ padding: "6px 10px", borderRadius: 10, background: "var(--layer1)", border: "1px solid var(--border)" }}><div style={{ fontSize: 8, color: "var(--muted)", fontWeight: 700, marginBottom: 2 }}>Vega</div><div style={{ fontSize: 12, fontWeight: 800, color: "var(--text)" }}>{fmtN(greeks.vega, 4)}</div></div>}
              {greeks.iv != null && <div style={{ padding: "6px 10px", borderRadius: 10, background: "var(--layer1)", border: "1px solid var(--border)" }}><div style={{ fontSize: 8, color: "var(--muted)", fontWeight: 700, marginBottom: 2 }}>IV</div><div style={{ fontSize: 12, fontWeight: 800, color: "#fbbf24" }}>{fmtN(greeks.iv * 100, 0)}%</div></div>}
            </div>
          </div>
        )}

        {/* ── Omega AI Signals ── */}
        {signals.length > 0 && (
          <div style={{ margin: "12px 24px 0", padding: 14, borderRadius: 16, background: "rgba(167,139,250,0.04)", border: "1px solid rgba(167,139,250,0.15)" }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "#a78bfa", marginBottom: 8 }}>Omega AI Signals</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {signals.map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 7, fontSize: 11, color: "var(--text)", lineHeight: 1.5 }}>
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: s.includes("URGENT") || s.includes("Sharp") || s.includes("EXPIRING") ? "#f87171" : s.includes("Surging") || s.includes("ITM") || s.includes("bullish") ? "#4ade80" : "#a78bfa", flexShrink: 0, marginTop: 5, display: "inline-block" }} />
                  {s}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── TEA sentiment ── */}
        {tea?.rationale && (
          <div style={{ margin: "10px 24px 0", padding: "10px 14px", borderRadius: 14, background: `${teaColor(tea.teaScore)}08`, border: `1px solid ${teaColor(tea.teaScore)}20`, display: "flex", alignItems: "flex-start", gap: 8 }}>
            <span style={{ fontSize: 9, fontWeight: 800, color: teaColor(tea.teaScore), flexShrink: 0, marginTop: 1 }}>TEA · {(tea.sentiment || "").toUpperCase()}</span>
            <span style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.5 }}>{tea.rationale}</span>
          </div>
        )}

        {/* ── Quick Actions ── */}
        <div style={{ padding: "14px 24px 0", display: "flex", gap: 8 }}>
          <button onClick={() => { onClose(); }} style={{ flex: 1, padding: "11px 0", borderRadius: 14, border: "1px solid rgba(248,113,113,0.3)", background: "rgba(248,113,113,0.08)", color: "#f87171", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>Sell</button>
          <button onClick={() => { onClose(); }} style={{ flex: 1, padding: "11px 0", borderRadius: 14, border: "1px solid rgba(251,191,36,0.3)", background: "rgba(251,191,36,0.08)", color: "#fbbf24", fontSize: 12, fontWeight: 800, cursor: "pointer" }}>Trail Stop</button>
        </div>

        <div style={{ height: 24 }} />
      </div>
    </div>
  );
}

// ─── Watch Room Modal ─────────────────────────────────────────────────────────
const WATCH_ACTION_COLORS = ACTION_COLORS;

function WatchRoomModal({ pos, onClose, onQuickTrade, onTrailStop }) {
  // For options, use underlying ticker for chart; for stocks/crypto use symbol directly
  const chartSym = pos.ticker || pos.symbol;
  const action = pos.action || "HOLD";
  const actionColor = WATCH_ACTION_COLORS[action] || "#818cf8";
  const pct = pos.dayGainPct ?? pos.gainPct ?? 0;
  const pctPos = pct >= 0;

  const [modalChart, setModalChart] = useState(null);
  const [modalChartLoading, setModalChartLoading] = useState(true);
  const [chartSource, setChartSource] = useState("");

  const fmtN = (v, d = 2) => v == null ? "—" : Number(v).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

  useEffect(() => {
    let cancelled = false;

    async function fetchChart() {
      setModalChartLoading(true);
      setModalChart(null);
      setChartSource("");

      const toChartData = (bars) => ({
        labels: bars.map(b => {
          const ms = b.t > 1e12 ? b.t : b.t * 1000;
          return new Date(ms).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
        }),
        prices: bars.map(b => b.c).filter(v => v != null),
      });

      // 1. Nasdaq via /market/chart (most reliable)
      try {
        const r = await axios.get(`${API}/market/chart`, { params: { symbol: chartSym, range: "1D" } });
        const bars = r.data?.bars || [];
        if (!cancelled && bars.length > 1) {
          setModalChart(toChartData(bars));
          setChartSource("Nasdaq · 5m");
          setModalChartLoading(false);
          return;
        }
      } catch (e) { /* try next */ }

      // 2. Live tick accumulator
      try {
        const r = await axios.get(`${API}/ticks/history`, { params: { symbol: chartSym } });
        const bars = r.data?.bars || [];
        if (!cancelled && bars.length > 1) {
          setModalChart(toChartData(bars));
          setChartSource("Live ticks");
          setModalChartLoading(false);
          return;
        }
      } catch (e) { /* no data */ }

      if (!cancelled) {
        setModalChart(null);
        setModalChartLoading(false);
      }
    }

    fetchChart();
    const iv = setInterval(fetchChart, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [chartSym]);

  const chartData = modalChart?.prices?.length ? {
    labels: modalChart.labels,
    datasets: [{
      label: chartSym,
      data: modalChart.prices,
      borderColor: pctPos ? "#22c55e" : "#f87171",
      backgroundColor: pctPos ? "rgba(34,197,94,0.08)" : "rgba(248,113,113,0.08)",
      fill: true, pointRadius: 0, borderWidth: 2, tension: 0.4,
    }],
  } : null;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: 24, border: "1px solid var(--border)", width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 80px rgba(0,0,0,0.18)" }}>
        {/* Header */}
        <div style={{ padding: "22px 24px 0", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 6 }}>Omega AI Watch Room</div>
            <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: "-0.03em", color: "var(--text)", lineHeight: 1 }}>{pos.display || chartSym}</div>
            {pos.type === "OPTION" && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>{pos.side} · Strike ${fmtN(pos.strike, 2)} · Exp {pos.expDate} · {pos.dte}d</div>}
            {/* TEA score + trail chip row */}
            <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
              {pos.teaScore != null && (
                <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
                  background: pos.teaScore >= 70 ? "rgba(74,222,128,0.15)" : pos.teaScore >= 40 ? "rgba(250,204,21,0.15)" : "rgba(239,68,68,0.15)",
                  color: pos.teaScore >= 70 ? "#4ade80" : pos.teaScore >= 40 ? "#fbbf24" : "#f87171",
                }}>TEA {pos.teaScore} · {pos.teaSentiment || "neutral"}</span>
              )}
              {pos.ziSignal?.bias && (
                <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
                  background: pos.ziSignal.bias === "BULLISH" ? "rgba(34,197,94,0.12)" : pos.ziSignal.bias === "BEARISH" ? "rgba(248,113,113,0.12)" : "rgba(167,139,250,0.12)",
                  color: pos.ziSignal.bias === "BULLISH" ? "#22c55e" : pos.ziSignal.bias === "BEARISH" ? "#f87171" : "#a78bfa",
                }}>Ω {pos.ziSignal.bias}</span>
              )}
              {pos.trailStop && (
                <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 4, background: "rgba(74,222,128,0.15)", color: "#4ade80", border: "1px solid rgba(74,222,128,0.3)" }}>TRAIL ARMED</span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: "50%", border: "1px solid var(--border)", background: "var(--layer1)", color: "var(--muted)", fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
            <div style={{ fontSize: 11, fontWeight: 800, color: actionColor, background: `${actionColor}15`, border: `1px solid ${actionColor}30`, borderRadius: 8, padding: "4px 10px" }}>{action}</div>
          </div>
        </div>

        {/* Live chart */}
        <div style={{ margin: "18px 24px 0" }}>
          <div style={{ height: 140, borderRadius: 16, overflow: "hidden", background: "var(--layer1)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
            {modalChartLoading
              ? <span style={{ fontSize: 11, color: "var(--dim)", display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", display: "inline-block", animation: "pulse 1s infinite" }} />
                  Loading live chart…
                </span>
              : chartData
                ? <Line data={chartData} options={{ responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { display: false }, tooltip: { mode: "index", intersect: false } }, scales: { x: { display: false }, y: { display: false } } }} style={{ width: "100%", height: "100%" }} />
                : <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 11, color: "var(--dim)", marginBottom: 8 }}>Chart unavailable</div>
                    <button onClick={() => { setModalChartLoading(true); setModalChart(null); }} style={{ fontSize: 10, padding: "4px 10px", borderRadius: 6, background: "rgba(96,165,250,0.1)", border: "1px solid rgba(96,165,250,0.3)", color: "#60a5fa", cursor: "pointer" }}>Retry</button>
                  </div>}
          </div>
          {chartData && chartSource && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4, paddingLeft: 4, paddingRight: 4 }}>
              <span style={{ fontSize: 9, color: "var(--dim)", display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#22c55e", display: "inline-block", animation: "pulse 2s infinite" }} />
                {chartSym} · 5m intraday · {chartSource}
              </span>
              <span style={{ fontSize: 9, color: "var(--dim)" }}>Refreshes every 30s</span>
            </div>
          )}
        </div>

        {/* Price row */}
        <div style={{ padding: "16px 24px 0", display: "flex", gap: 12 }}>
          {[
            { label: "Price", val: pos.lastPrice != null ? `$${fmtN(pos.lastPrice, pos.lastPrice < 1 ? 4 : 2)}` : "—" },
            { label: "Day P&L", val: pos.dayGainValue != null ? `${pos.dayGainValue >= 0 ? "+" : ""}$${fmtN(pos.dayGainValue, 2)}` : "—", color: (pos.dayGainValue || 0) >= 0 ? "var(--green)" : "var(--red)" },
            { label: "Day %", val: `${pctPos ? "+" : ""}${fmtN(pct, 2)}%`, color: pctPos ? "var(--green)" : "var(--red)" },
            { label: "Value", val: pos.currentValue != null ? `$${fmtN(pos.currentValue, 2)}` : "—" },
          ].map(({ label, val, color }) => (
            <div key={label} style={{ flex: 1, padding: "10px 12px", borderRadius: 12, background: "var(--layer1)", border: "1px solid var(--border)", textAlign: "center" }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: color || "var(--text)" }}>{val}</div>
            </div>
          ))}
        </div>

        {/* Greeks (options only) */}
        {pos.greeks && (
          <div style={{ padding: "14px 24px 0" }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 8 }}>Greeks</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {[
                { k: "Delta", v: fmtN(pos.greeks.delta, 4) },
                { k: "Gamma", v: fmtN(pos.greeks.gamma, 4) },
                { k: "Theta", v: `$${fmtN(Math.abs(pos.greeks.theta || 0) * 100, 2)}/day` },
                { k: "Vega", v: fmtN(pos.greeks.vega, 4) },
                { k: "IV", v: `${fmtN((pos.greeks.iv || 0) * 100, 1)}%` },
              ].map(({ k, v }) => (
                <div key={k} style={{ padding: "7px 12px", borderRadius: 10, background: "var(--layer1)", border: "1px solid var(--border)" }}>
                  <div style={{ fontSize: 9, color: "var(--muted)", fontWeight: 700, marginBottom: 2 }}>{k}</div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "var(--text)" }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI Signals */}
        {pos.signals?.length > 0 && (
          <div style={{ padding: "14px 24px 0" }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 8 }}>Omega ZI Signals</div>
            <div style={{ borderRadius: 14, border: "1px solid rgba(129,140,248,0.2)", background: "rgba(129,140,248,0.05)", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
              {pos.signals.map((s, idx) => (
                <div key={idx} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "var(--text)", lineHeight: 1.5 }}>
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#818cf8", flexShrink: 0, marginTop: 5, display: "inline-block" }} />
                  {s}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Option chain extras */}
        {pos.type === "OPTION" && (
          <div style={{ padding: "14px 24px 0", display: "flex", gap: 8, flexWrap: "wrap" }}>
            {pos.breakeven != null && <div style={{ padding: "7px 12px", borderRadius: 10, background: "var(--layer1)", border: "1px solid var(--border)" }}><div style={{ fontSize: 9, color: "var(--muted)", fontWeight: 700, marginBottom: 2 }}>Breakeven</div><div style={{ fontSize: 12, fontWeight: 800 }}>${fmtN(pos.breakeven, 2)}</div></div>}
            {pos.moneyness != null && <div style={{ padding: "7px 12px", borderRadius: 10, background: "var(--layer1)", border: "1px solid var(--border)" }}><div style={{ fontSize: 9, color: "var(--muted)", fontWeight: 700, marginBottom: 2 }}>Moneyness</div><div style={{ fontSize: 12, fontWeight: 800, color: pos.moneyness < 0 ? "var(--red)" : "var(--green)" }}>{fmtN(pos.moneyness, 1)}%</div></div>}
            {pos.stockPrice != null && <div style={{ padding: "7px 12px", borderRadius: 10, background: "var(--layer1)", border: "1px solid var(--border)" }}><div style={{ fontSize: 9, color: "var(--muted)", fontWeight: 700, marginBottom: 2 }}>Stock Price</div><div style={{ fontSize: 12, fontWeight: 800 }}>${fmtN(pos.stockPrice, 2)}</div></div>}
            {pos.optionVolume != null && <div style={{ padding: "7px 12px", borderRadius: 10, background: "var(--layer1)", border: "1px solid var(--border)" }}><div style={{ fontSize: 9, color: "var(--muted)", fontWeight: 700, marginBottom: 2 }}>Volume</div><div style={{ fontSize: 12, fontWeight: 800 }}>{pos.optionVolume?.toLocaleString()}</div></div>}
          </div>
        )}

        {/* Quick-trade buttons */}
        {(onQuickTrade || onTrailStop) && (
          <div style={{ padding: "14px 24px 0", display: "flex", gap: 8 }}>
            {onQuickTrade && (
              <button onClick={() => { onClose(); onQuickTrade(pos, "sell"); }}
                style={{ flex: 1, padding: "10px 0", borderRadius: 10, background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.28)", color: "#f87171", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                Sell
              </button>
            )}
            {onQuickTrade && (
              <button onClick={() => { onClose(); onQuickTrade(pos, "buy_more"); }}
                style={{ flex: 1, padding: "10px 0", borderRadius: 10, background: "rgba(74,222,128,0.10)", border: "1px solid rgba(74,222,128,0.28)", color: "#4ade80", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                Buy More
              </button>
            )}
            {onTrailStop && (
              <button onClick={() => { onClose(); onTrailStop(pos); }}
                style={{ flex: 1, padding: "10px 0", borderRadius: 10, background: "rgba(251,191,36,0.10)", border: "1px solid rgba(251,191,36,0.28)", color: "#fbbf24", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                Trail Stop
              </button>
            )}
          </div>
        )}

        <div style={{ height: 24 }} />
      </div>
    </div>
  );
}


function EquitySparkline({ data, color, width = 200, height = 44 }) {
  if (!data || data.length < 2) return (
    <div style={{ width, height, background: "rgba(255,255,255,0.03)", borderRadius: 6 }} />
  );
  const valid = data.filter(v => v != null && isFinite(v));
  if (valid.length < 2) return <div style={{ width, height }} />;
  const min = Math.min(...valid);
  const max = Math.max(...valid);
  const range = max - min || Math.abs(min) * 0.01 || 1;
  const PAD = 3;
  const pts = valid.map((v, i) => [
    PAD + (i / (valid.length - 1)) * (width - PAD * 2),
    PAD + (height - PAD * 2) - ((v - min) / range) * (height - PAD * 2),
  ]);
  const dPath = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const fillPath = `${dPath} L${(width - PAD).toFixed(1)},${height} L${PAD},${height} Z`;
  const gradId = `eqspark-${Math.round(Date.now() / 10000)}`;
  const last = pts[pts.length - 1];
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: "block", overflow: "visible" }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={fillPath} fill={`url(#${gradId})`} />
      <path d={dPath} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
      {last && <circle cx={last[0]} cy={last[1]} r="3" fill={color} />}
    </svg>
  );
}


// ─── Account Tab ──────────────────────────────────────────────────────────────
export default function AccountTab({ quoteData, regime, omegaStatus, omegaDispatch, portfolio, portfolioStale, refreshPortfolio }) {
  // portfolio/portfolioStale now come from App.js's own hero-strip state
  // (props above) instead of a second, independent GET /portfolio poll on
  // its own 30s timer — the two were unsynchronized, doubling backend load
  // whenever this tab was open and letting the hero strip and this tab's
  // own equity cards disagree for up to 30s after a real balance change.
  const [positions, setPositions] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [chartSymbol, setChartSymbol] = useState("SPY");
  const [history, setHistory] = useState([]);
  const [chartError, setChartError] = useState(false);
  const [milestones, setMilestones] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [goals, setGoals] = useState([]);
  const [accountInsight, setAccountInsight] = useState(null);
  const [detailPosition, setDetailPosition] = useState(null);
  const [openPosition, setOpenPosition] = useState(null);
  const [quickTrade, setQuickTrade] = useState(null); // { position, mode: "sell" | "buy_more" }
  const [trailStop, setTrailStop] = useState(null); // { position }
  const [newGoal, setNewGoal] = useState({ title: "", target: "", timeframe: "month", metric: "pnl" });
  const [goalModal, setGoalModal] = useState(null); // selected goal for detail modal
  const [goalStrategy, setGoalStrategy] = useState({}); // { [goal_id]: { loading, text } }
  const [addGoalOpen, setAddGoalOpen] = useState(false);
  const [chartEvent, setChartEvent] = useState(null);
  const [metricModal, setMetricModal] = useState(null); // null | "equity" | "bp" | "obp" | "pnl"

  const [dailyPnl, setDailyPnl] = useState(null);
  const [pnlExpanded, setPnlExpanded] = useState(false);
  const [pnlPeriod, setPnlPeriod] = useState("today");
  const [pnlForecast, setPnlForecast] = useState("");
  const [pnlForecastLoading, setPnlForecastLoading] = useState(false);
  const [recentOrders, setRecentOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [zbucksPositions, setZbucksPositions] = useState([]);

  useEffect(() => {
    const load = () => {
      axios.get(`${API}/zbucks/user/positions`).then(r => setZbucksPositions(r.data?.positions || [])).catch(() => {});
    };
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  const loadOrders = async () => {
    setOrdersLoading(true);
    try {
      const r = await axios.get(`${API}/orders/recent`);
      setRecentOrders(r.data?.orders || []);
    } catch { setRecentOrders([]); }
    setOrdersLoading(false);
  };

  const checkOrderStatus = async (orderId) => {
    try {
      const r = await axios.get(`${API}/order/status/${orderId}`);
      if (r.data?.ok && r.data.order) {
        setRecentOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, status: r.data.order.status || o.status, filled_quantity: r.data.order.filled_quantity, average_price: r.data.order.average_price } : o));
      }
    } catch {}
  };

  const [omegaWatch, setOmegaWatch] = useState(null);
  const [watchRoomModal, setWatchRoomModal] = useState(null); // selected position for popup
  const [lifeChart, setLifeChart] = useState(null);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [eventInsight, setEventInsight] = useState(null);
  const [eventInsightLoading, setEventInsightLoading] = useState(false);
  const [chartRange, setChartRange] = useState("Month");
  const [intradayPoints, setIntradayPoints] = useState([]);
  const [omegaAiLine, setOmegaAiLine] = useState(null);
  const [showOmegaAiLine, setShowOmegaAiLine] = useState(true);
  const liveEquityRef = useRef(0);
  const isMarketOpenRef = useRef(false);
  const intradayPtsRef = useRef([]);
  const liveChartRef = useRef(null);
  const livePnlRef = useRef({ val: 0, pct: 0 }); // stable ref read by endpointPlugin
  // Stable data object — same reference every render, updated imperatively
  const intradayChartDataRef = useRef(null);
  // Per-5s-interval trend derived from lifeChart.forecast (daily → intraday slope)
  const intradayTrendRef = useRef(0);

  const [marketStatus, setMarketStatus] = useState(null);
  const loadMarketStatus = async () => { try { const r = await axios.get(`${API}/omega/market-status`); setMarketStatus(r.data); } catch {} };

  const loadOmegaWatch = async () => { try { const r = await fastGetAsync(`${API}/omega/watch`); setOmegaWatch(r.data); } catch {} };
  const loadLifeChart = async () => { try { const r = await axios.get(`${API}/account/life-chart`); setLifeChart(r.data); } catch {} };
  const loadOmegaAiLine = async (horizon = "month") => {
    try {
      // Real account forecast (Monte Carlo, fed by this account's own real
      // win rate/avg win-loss) — via axios so the app's normal session
      // cookie + CSRF header are attached. Previously called
      // /api/omega/v2/zbucks/forecast with a raw fetch() that carried
      // neither, so it always failed "Not authenticated" and rendered as
      // fabricated-looking 0.0% placeholders — and even fixed, that
      // endpoint pulled from an unrelated, never-funded paper account
      // rather than this real one.
      const r = await axios.get(`${API}/account/forecast?horizon=${horizon}`);
      setOmegaAiLine(r.data);
    } catch {
      setOmegaAiLine(null);
    }
  };

  useEffect(() => { loadAccountData(); loadPerfChart("SPY"); loadMilestones(); loadGoals(); loadDailyPnl(); loadOrders(); loadOmegaWatch(); loadLifeChart(); loadOmegaAiLine("month"); loadMarketStatus(); }, []);

  // loadAccountData (positions + transaction history) previously only ran
  // once on mount — left the tab open for a while and it never caught up
  // with real account changes on its own. Poll it like the rest of this
  // tab's live data does, and refetch immediately on refocus since
  // backgrounded tabs can silently miss/suspend setInterval ticks entirely
  // (same fix as the dashboard hero strip in App.js, which independently
  // keeps the portfolio/equity prop above fresh on its own timer).
  useEffect(() => {
    const iv = setInterval(loadAccountData, 30000);
    const onVisible = () => { if (document.visibilityState === "visible") loadAccountData(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVisible); };
  }, []);

  const loadDailyPnl = async (period) => {
    try { const r = await fastGetAsync(`${API}/daily-pnl?period=${period || pnlPeriod}`); setDailyPnl(r.data); } catch {}
  };

  // Real-time SSE stream: portfolio P&L every 3s
  useEffect(() => {
    // openSSE: silent-stream watchdog + reconnect (this had no onerror at all).
    const conn = openSSE(`${API}/stream/portfolio-pnl`, { pnl: (d) => {
      try {
        setDailyPnl(prev => {
          // Append new equity point to sparkline (max 48 points)
          const prevSparkline = prev?.sparkline || [];
          const newEquity = d.totalEquity;
          const updatedSparkline = newEquity
            ? [...prevSparkline, newEquity].slice(-48)
            : prevSparkline;
          const updated = {
            ...prev,
            totalDayPnl: d.totalDayPnl,
            dayPnlPct: d.dayPnlPct,
            totalEquity: d.totalEquity,
            positions: d.positions,
            sparkline: updatedSparkline,
          };
          // Preserve period data from a prior period fetch
          if (prev?.periodPnl != null) updated.periodPnl = prev.periodPnl;
          if (prev?.periodTrades) updated.periodTrades = prev.periodTrades;
          if (prev?.period) updated.period = prev.period;
          return updated;
        });
      } catch {}
    } });
    return () => { conn.close(); };
  }, []);

  // Reload when period changes
  useEffect(() => { loadDailyPnl(pnlPeriod); }, [pnlPeriod]);

  // Keep liveEquityRef in sync — read inside intervals without stale closure
  // Also seeds/updates the Day chart the moment real equity arrives
  useEffect(() => {
    const eq = dailyPnl?.totalEquity || lifeChart?.currentEquity || 0;
    if (!eq) return;
    const wasZero = !liveEquityRef.current;
    liveEquityRef.current = eq;
    // If Day view is active and we had no equity yet, seed the chart now
    if (wasZero && chartRange === "Day") {
      const label = new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
      setIntradayPoints([
        { label: "00:00", equity: eq, isOpen: true },
        { label, equity: eq },
      ]);
    }
  }, [dailyPnl?.totalEquity, lifeChart?.currentEquity, chartRange]);

  // Live intraday — seeds on Day entry, then every 5s mutates the stable chart data ref
  useEffect(() => {
    if (chartRange !== "Day") {
      intradayPtsRef.current = [];
      intradayChartDataRef.current = null;
      setIntradayPoints([]);
      return;
    }

    const makeLabel = () =>
      new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });

    // ── Helper: compute chart arrays from pts ────────────────────────────────
    const buildArrays = (pts) => {
      const valid = pts.filter(p => p.equity > 0);
      if (valid.length < 2) return null;
      const open = valid[0].equity;
      const cur  = valid[valid.length - 1].equity;
      const n    = valid.length;
      const up   = cur >= open;
      const mo   = isMarketOpenRef.current;
      const lc   = !mo ? "#60a5fa" : up ? "#22c55e" : "#f87171";
      const fill = !mo ? "rgba(96,165,250,0.08)" : up ? "rgba(34,197,94,0.10)" : "rgba(248,113,113,0.10)";
      const fcCount = Math.max(8, Math.ceil(n / 3)); // at least 8 steps = 40 min ahead
      const recent  = valid.slice(-Math.min(5, n));
      const rawT    = recent.length > 1 ? (recent[recent.length-1].equity - recent[0].equity) / (recent.length - 1) : 0;
      // Fall back to account-level daily trend when intraday data is flat
      const baseT   = Math.abs(rawT) > 0.01 ? rawT * 0.5 : intradayTrendRef.current;
      const dT      = Math.max(-cur * 0.005, Math.min(cur * 0.005, baseT));
      const [lh, lm] = valid[n-1].label.split(":").map(Number);
      let bm = lh * 60 + lm;
      const fcL = [], fcE = [];
      for (let i = 1; i <= fcCount; i++) {
        bm += 5;
        fcL.push(`${String(Math.floor(bm/60)%24).padStart(2,"0")}:${String(bm%60).padStart(2,"0")}:00`);
        fcE.push(Math.max(0, Math.round((cur + dT * i) * 100) / 100));
      }
      const allL = [...valid.map(p => p.label), ...fcL];
      const eqD  = [...valid.map(p => p.equity), ...fcL.map(() => null)];
      const fD   = [...valid.map((_, i) => i === n-1 ? cur : null), ...fcE];
      return { allL, eqD, fD, open, lc, fill, n };
    };

    // ── Push computed arrays into the stable chart data object ───────────────
    const applyToData = (d, a) => {
      if (!d || !a) return;
      d.labels = a.allL;
      d.datasets[0].data = a.eqD;
      d.datasets[0].borderColor = a.lc;
      d.datasets[0].backgroundColor = a.fill;
      d.datasets[0].pointRadius = a.eqD.map((v, i) => v != null && i === a.n - 1 ? 5 : 0);
      d.datasets[0].pointBackgroundColor = a.eqD.map((v, i) => v != null && i === a.n - 1 ? a.lc : "transparent");
      d.datasets[0].pointBorderColor     = a.eqD.map((v, i) => v != null && i === a.n - 1 ? "white" : "transparent");
      d.datasets[1].data = a.fD;
      d.datasets[2].data = a.allL.map(() => a.open);
    };

    // ── Seed: build stable data object + trigger first render ────────────────
    const openEq = liveEquityRef.current || 0;
    const seed = [
      { label: "00:00", equity: openEq, isOpen: true },
      { label: makeLabel(), equity: openEq },
    ];
    intradayPtsRef.current = seed;

    // Build initial stable data object with forecast already populated
    const initArrays = buildArrays(seed);
    if (initArrays) {
      const { allL, eqD, fD, open: op, lc: ilc, fill: ifill } = initArrays;
      intradayChartDataRef.current = {
        labels: allL,
        datasets: [
          { label: "Equity", data: eqD, borderColor: ilc, backgroundColor: ifill, fill: true, pointRadius: eqD.map((v,i)=>v!=null&&i===1?5:0), pointBackgroundColor: eqD.map((v,i)=>v!=null&&i===1?ilc:"transparent"), pointBorderColor: eqD.map((v,i)=>v!=null&&i===1?"white":"transparent"), pointBorderWidth: 2, pointHoverRadius: 7, borderWidth: 2.5, tension: 0.4 },
          { label: "Trend (recent slope)", data: fD, borderColor: "#818cf8", borderDash: [6, 4], backgroundColor: "rgba(129,140,248,0.07)", fill: false, pointRadius: 0, borderWidth: 1.8, tension: 0.35 },
          { label: "Open", data: allL.map(()=>op), borderColor: "rgba(251,191,36,0.45)", borderDash: [4, 4], pointRadius: 0, borderWidth: 1, tension: 0, fill: false },
        ],
      };
    } else {
      intradayChartDataRef.current = {
        labels: ["00:00", seed[1].label],
        datasets: [
          { label: "Equity", data: [openEq, openEq], borderColor: "#60a5fa", backgroundColor: "rgba(96,165,250,0.08)", fill: true, pointRadius: [0, 5], pointBackgroundColor: ["transparent","#60a5fa"], pointBorderColor: ["transparent","white"], pointBorderWidth: 2, pointHoverRadius: 7, borderWidth: 2.5, tension: 0.4 },
          { label: "Trend (recent slope)", data: [null, openEq], borderColor: "#818cf8", borderDash: [6,4], backgroundColor: "rgba(129,140,248,0.07)", fill: false, pointRadius: 0, borderWidth: 1.8, tension: 0.35 },
          { label: "Open", data: [openEq, openEq], borderColor: "rgba(251,191,36,0.45)", borderDash: [4,4], pointRadius: 0, borderWidth: 1, tension: 0, fill: false },
        ],
      };
    }
    setIntradayPoints([...seed]); // one render to mount the chart

    const iv = setInterval(() => {
      const eq = liveEquityRef.current;
      if (!eq) return;
      const label = makeLabel();
      const pts   = intradayPtsRef.current;
      const live  = pts.filter(p => !p.isOpen);
      if (live.length && live[live.length - 1].label === label) {
        live[live.length - 1].equity = eq;
      } else {
        pts.push({ label, equity: eq });
      }

      // Mutate the stable data object + update chart — zero React involvement
      const arrays = buildArrays(pts);
      applyToData(intradayChartDataRef.current, arrays);
      liveChartRef.current?.update("none");

      // React re-render only when direction flips (hero card color)
      if (arrays) {
        setIntradayPoints(prev => {
          const pv = prev.filter(p => p.equity > 0);
          const prevUp = pv.length >= 2 ? pv[pv.length-1].equity >= pv[0].equity : null;
          const curUp  = arrays.eqD.filter(Boolean).slice(-1)[0] >= arrays.open;
          return prevUp === curUp ? prev : pts.slice();
        });
      }
    }, 5_000);

    return () => clearInterval(iv);
  }, [chartRange]);

  // Once lifeChart loads: patch open anchor + derive intraday trend from forecast
  useEffect(() => {
    if (!lifeChart?.timeline?.length) return;
    const tl = lifeChart.timeline;
    const fc = lifeChart.forecast || [];
    const current = tl[tl.length - 1]?.equity || 0;
    // Derive per-5s slope from 7-day forecast trend
    if (fc.length >= 7 && current > 0) {
      const fc7 = fc.find(f => f.day === 7) || fc[6];
      if (fc7) {
        const dailyDelta = (fc7.equity - current) / 7;     // $ per day
        intradayTrendRef.current = dailyDelta / (24 * 12); // $ per 5-min step
      }
    }
    if (chartRange !== "Day") return;
    const openEq = tl.length >= 2 ? tl[tl.length - 2].equity : tl[0].equity;
    if (!openEq) return;
    intradayPtsRef.current = intradayPtsRef.current.map(p => p.isOpen ? { ...p, equity: openEq } : p);
    setIntradayPoints(prev => prev.length ? prev.map(p => p.isOpen ? { ...p, equity: openEq } : p) : prev);
  }, [lifeChart, chartRange]);

  // Sync trades from Public.com on mount
  useEffect(() => { axios.get(`${API}/trades/sync`).catch(() => {}); }, []);

  // Hydrate favorites from backend on login (cross-device continuity)
  useEffect(() => {
    import("../utils/symbolCache").then(m => m.loadFavoritesFromBackend?.());
  }, []);

  const loadGoals = async () => {
    const [gRes, iRes] = await Promise.allSettled([
      axios.get(`${API}/goals`),
      axios.get(`${API}/account/insight`),
    ]);
    if (gRes.status === "fulfilled") setGoals(Array.isArray(gRes.value.data) ? gRes.value.data : []);
    if (iRes.status === "fulfilled") setAccountInsight(iRes.value.data);
  };

  const createGoal = async () => {
    if (!newGoal.title || !newGoal.target) return;
    try {
      const r = await axios.post(`${API}/goals`, newGoal);
      setGoals(prev => [...prev, r.data]);
      setNewGoal({ title: "", target: "", timeframe: "month", metric: "pnl" });
    } catch {}
  };

  const deleteGoal = async (id) => {
    try { await axios.delete(`${API}/goals/${id}`); setGoals(prev => prev.filter(g => g.goal_id !== id)); } catch {}
  };

  const fetchGoalStrategy = async (goalId) => {
    setGoalStrategy(prev => ({ ...prev, [goalId]: { loading: true, text: null } }));
    try {
      const r = await axios.post(`${API}/goals/${goalId}/ai-strategy`);
      setGoalStrategy(prev => ({ ...prev, [goalId]: { loading: false, text: r.data?.strategy || "No strategy returned." } }));
    } catch {
      setGoalStrategy(prev => ({ ...prev, [goalId]: { loading: false, text: "Failed to load strategy." } }));
    }
  };

  const loadAccountData = async () => {
    // Portfolio/equity data comes from App.js's own poll now (see the
    // portfolio/portfolioStale props) — this only owns what's genuinely
    // specific to this tab.
    const [qRes, hRes] = await Promise.allSettled([
      axios.get(`${API}/market/quotes?symbols=SPY,QQQ,AAPL,TSLA,NVDA,AAL`),
      axios.get(`${API}/portfolio/history?page_size=50`),
    ]);
    if (qRes.status === "fulfilled" && Array.isArray(qRes.value.data)) setPositions(qRes.value.data);
    if (hRes.status === "fulfilled") {
      const hd = hRes.value.data;
      // History endpoint now returns { transactions, count, totalIn, totalOut, net }
      setHistory(Array.isArray(hd) ? hd : (hd?.transactions || []));
    }
  };

  const loadMilestones = async () => {
    try {
      const r = await axios.get(`${API}/milestones/enhanced`);
      setMilestones(r.data?.milestones || []);
    } catch {
      // Fallback to local milestones
      const events = [];
      events.push({ type: "system", ts: new Date().toISOString(), title: "Omega ZI AI 6.0 Active", color: "#60a5fa", data: { version: "5.1" } });
      setMilestones(events);
    }
  };

  const loadPerfChart = async (sym) => {
    setChartError(false);
    setChartData(null);
    try {
      const r = await axios.get(`${API}/market/ohlcv?symbol=${sym}&days=90`);
      const bars = r.data?.bars || [];
      if (!bars.length) { setChartError(true); return; }
      const labels = bars.map(b => new Date(b.t).toLocaleDateString("en-US", { month: "short", day: "numeric" }));
      const closes = bars.map(b => b.c);
      const ma20 = closes.map((_, i) => i < 19 ? null : closes.slice(i - 19, i + 1).reduce((a, b) => a + b, 0) / 20);
      // Zi Forecast: 10-day trend projection
      const last10 = closes.slice(-10);
      const trend = (last10[last10.length - 1] - last10[0]) / 10;
      const projLabels = [];
      const projData = [...closes.map(() => null)];
      projData[projData.length - 1] = closes[closes.length - 1];
      for (let d = 1; d <= 10; d++) {
        projLabels.push(`+${d}d`);
        projData.push(Math.round((closes[closes.length - 1] + trend * d) * 100) / 100);
      }
      setChartData({
        labels: [...labels, ...projLabels],
        datasets: [
          { label: sym, data: [...closes, ...projLabels.map(() => null)], borderColor: "#60a5fa", backgroundColor: "rgba(96,165,250,0.08)", fill: true, pointRadius: 0, borderWidth: 1.5, tension: 0.3 },
          { label: "MA20", data: [...ma20, ...projLabels.map(() => null)], borderColor: "rgba(250,204,21,0.5)", borderDash: [4, 4], pointRadius: 0, borderWidth: 1, tension: 0.3 },
          { label: "Trend (10-day)", data: projData, borderColor: "rgba(74,222,128,0.7)", borderDash: [3, 3], pointRadius: 0, borderWidth: 1.5, tension: 0.3, backgroundColor: "rgba(74,222,128,0.05)", fill: true },
        ],
      });
    } catch { setChartError(true); }
  };

  const chartOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: true, labels: { color: "var(--muted)", boxWidth: 10, font: { size: 10 } } }, tooltip: { mode: "index", intersect: false, backgroundColor: "var(--surface)", titleColor: "var(--text)", bodyColor: "var(--muted)" } },
    scales: { x: { ticks: { color: "var(--dim)", font: { size: 9 }, maxTicksLimit: 10 }, grid: { color: "var(--grid)" } }, y: { ticks: { color: "var(--dim)", font: { size: 9 } }, grid: { color: "var(--grid)" } } },
    interaction: { mode: "nearest", axis: "x" },
  };

  // Derive position-level AI insight from P&L data
  const posInsight = (p) => {
    const pct = p.dayPnlPct || 0;
    if (pct > 25) return "Strong momentum — scale out 50% to lock gains.";
    if (pct > 10) return "Profitable today — tighten trail stop to protect.";
    if (pct >= 0) return "Slight gain — hold with trail stop at entry.";
    if (pct > -15) return "Modest drawdown — monitor for reversal signal.";
    return "Drawdown detected — Risk Gate active. Review position.";
  };

  const equity = portfolio?.totalEquity || dailyPnl?.totalEquity || 0;
  // Derive yesterday's close early so we can compute accurate day P&L.
  // The broker's dayGainValue often carries the previous session until market open.
  const _ycl = (() => {
    const tl = lifeChart?.timeline || [];
    if (tl.length >= 2) return tl[tl.length - 2].equity;
    return lifeChart?.yesterday_close ?? lifeChart?.yesterdayClose ?? null;
  })();
  const totalReturn = equity > 0 && lifeChart?.timeline?.length > 1
    ? ((equity - lifeChart.timeline[0].equity) / lifeChart.timeline[0].equity * 100)
    : 0;

  // Market hours: 9:30 AM – 4:00 PM ET, Mon–Fri
  // Crypto trades 24/7 so we mark extended-hours open when crypto positions exist
  const isMarketOpen = (() => {
    const hasCrypto = (portfolio?.positions || dailyPnl?.positions || [])
      .some(p => {
        const sym = (p.symbol || p.rawSymbol || "").toUpperCase();
        return ["BTC","ETH","SOL","ADA","XRP","DOGE","AVAX","MATIC","DOT","LINK","LTC","BCH","UNI"].some(c => sym.includes(c));
      });
    if (hasCrypto) return true; // crypto never closes
    return isEquityOpen(); // one shared clock — see lib/marketClock
  })();
  isMarketOpenRef.current = isMarketOpen;

  // Today's P&L — zero when market is closed (no trading happened today).
  // When open, use yesterday's close from the life chart as the baseline so the
  // number reflects the actual intraday move and not a stale broker dayGainValue.
  const pnlVal = !isMarketOpen ? 0
    : (_ycl > 0 && equity > 0 ? equity - _ycl : (dailyPnl?.totalDayPnl || 0));
  const pnlPct = !isMarketOpen ? 0
    : (_ycl > 0 && equity > 0 ? ((equity - _ycl) / _ycl * 100) : (dailyPnl?.dayPnlPct || 0));
  const pnlPos = pnlVal >= 0;
  livePnlRef.current = { val: pnlVal, pct: pnlPct };

  // Intraday direction: is current equity above open anchor?
  const intradayUp = (() => {
    const valid = intradayPoints.filter(p => p.equity > 0);
    if (valid.length < 2) return null; // unknown
    return valid[valid.length - 1].equity >= valid[0].equity;
  })();

  // Hero color: green/red when market open & Day view, blue otherwise
  const heroColor = (() => {
    if (chartRange === "Day" && isMarketOpen && intradayUp !== null) {
      return intradayUp
        ? { bg: "rgba(34,197,94,0.05)", border: "rgba(34,197,94,0.18)", glow: "rgba(34,197,94,0.09)" }
        : { bg: "rgba(248,113,113,0.05)", border: "rgba(248,113,113,0.18)", glow: "rgba(248,113,113,0.09)" };
    }
    return { bg: "rgba(59,130,246,0.05)", border: "rgba(59,130,246,0.12)", glow: "rgba(59,130,246,0.1)" };
  })();

  const C = {
    root: { background: "transparent", width: "100%", color: "var(--text)", fontFamily: "'Outfit',sans-serif", boxSizing: "border-box" },
    hero: { background: heroColor.bg, border: `1px solid ${heroColor.border}`, borderRadius: 20, overflow: "hidden", position: "relative", margin: "0 16px 16px", transition: "background 0.8s ease, border-color 0.8s ease" },
    heroGlow: { position: "absolute", top: -80, right: -80, width: 360, height: 360, borderRadius: "50%", background: `radial-gradient(circle,${heroColor.glow},transparent 65%)`, pointerEvents: "none" },
    kicker: { fontSize: 10, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 12 },
    balance: { fontSize: "clamp(54px,8vw,90px)", fontWeight: 800, letterSpacing: "-0.05em", lineHeight: 0.88, color: "var(--text)" },
    chip: (pos, neutral) => ({ display: "inline-flex", alignItems: "center", padding: "9px 14px", borderRadius: 999, border: `1px solid ${neutral ? "var(--border)" : pos ? "rgba(74,222,128,0.3)" : "rgba(220,38,38,0.3)"}`, background: neutral ? "var(--layer1)" : pos ? "var(--green-bg)" : "var(--red-bg)", color: neutral ? "var(--muted)" : pos ? "var(--green)" : "var(--red)", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }),
    rangeBar: { display: "inline-flex", gap: 3, padding: 4, borderRadius: 999, border: "1px solid var(--border)", background: "var(--layer1)" },
    rangeBtn: (a) => ({ padding: "7px 13px", borderRadius: 999, background: a ? "var(--layer3)" : "transparent", color: a ? "var(--text)" : "var(--muted)", fontSize: 11, fontWeight: 700, border: "none", cursor: "pointer" }),
    sideCard: { padding: "16px 18px", borderRadius: 20, border: "1px solid var(--border)", background: "var(--layer1)", width: 240 },
    sideCardTitle: { fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 8 },
    sideCardValue: { fontSize: 22, fontWeight: 800, letterSpacing: "-0.03em", marginBottom: 6, color: "var(--text)" },
    sideCardBody: { fontSize: 12, color: "var(--muted)", lineHeight: 1.6 },
    chartZone: { marginTop: 22, borderRadius: "20px 20px 0 0", background: "var(--layer1)", overflow: "hidden" },
    eventPanel: { display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 14, padding: "16px 20px 20px", background: "transparent" },
    eventCard: { padding: "20px 22px", borderRadius: 20, border: "1px solid var(--border)", background: "var(--surface)" },
    metricRow: { display: "grid", gridTemplateColumns: "repeat(4,1fr)", background: "var(--surface)", borderBottom: "1px solid var(--border)" },
    metricCard: (accent) => ({ padding: "18px 22px", borderLeft: `2px solid ${accent}`, background: "transparent", cursor: "pointer", transition: "background 0.15s" }),
    metricLbl: { fontSize: 9, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 8 },
    metricNum: (color) => ({ fontSize: 24, fontWeight: 800, letterSpacing: "-0.04em", color, lineHeight: 1 }),
    metricSub: { fontSize: 11, color: "var(--dim)", marginTop: 5 },
    spark: { height: 28, marginTop: 10 },
    lower: { display: "grid", gridTemplateColumns: "1.1fr 1fr 0.95fr", gap: 16, padding: "0 16px 16px", width: "100%", boxSizing: "border-box" },
    secCard: { padding: "22px", borderRadius: 24, border: "1px solid var(--border)", background: "var(--surface)", overflow: "hidden", position: "relative" },
    secTitle: { fontSize: 22, fontWeight: 800, letterSpacing: "-0.03em", marginBottom: 4, color: "var(--text)" },
    secSub: { fontSize: 12, color: "var(--muted)", marginBottom: 16 },
    watchHero: { padding: "16px 18px", borderRadius: 18, border: "1px solid rgba(220,38,38,0.2)", background: "var(--red-bg)", marginBottom: 12 },
    watchRow: { display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center", padding: "13px 16px", borderRadius: 16, border: "1px solid var(--border)", background: "var(--layer1)", marginBottom: 8 },
    posRow: (neg) => ({ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center", padding: "13px 16px", borderRadius: 16, border: `1px solid ${neg ? "rgba(220,38,38,0.25)" : "var(--border)"}`, background: neg ? "var(--red-bg)" : "var(--layer1)", marginBottom: 8, cursor: "pointer" }),
    brkRow: { display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center", padding: "13px 16px", borderRadius: 16, border: "1px solid var(--border)", background: "var(--layer1)", marginBottom: 8 },
    rowSym: { fontSize: 14, fontWeight: 700, marginBottom: 4, color: "var(--text)" },
    rowSub: { fontSize: 11, color: "var(--muted)", lineHeight: 1.5 },
    rowVal: (color) => ({ fontSize: 17, fontWeight: 800, letterSpacing: "-0.02em", color, textAlign: "right", whiteSpace: "nowrap" }),
    watchChip: { display: "inline-flex", padding: "7px 12px", borderRadius: 999, background: "var(--layer2)", border: "1px solid var(--border)", color: "var(--muted)", fontSize: 11, fontWeight: 700, marginRight: 6, marginTop: 6 },
    brkBar: { height: 10, borderRadius: 999, overflow: "hidden", background: "var(--layer3)", display: "flex", marginTop: 14 },
  };

  const S = C;

  // Derive position display label matching mockup: "AAL $12C · 10 contracts"
  const posLabel = (p) => {
    const qty = p.quantity || p.qty || 1;
    const occ = String(p.type || "").toUpperCase().includes("OPTION") ? parseOCC(p.symbol) : null;
    if (occ) return `${occ.ticker} $${occ.strike} ${occ.side} · ${qty} contract${qty !== 1 ? "s" : ""}`;
    const name = p.name && p.name !== p.symbol ? p.name : p.symbol;
    return `${name} · ${qty} share${qty !== 1 ? "s" : ""}`;
  };
  const posSubLabel = (p) => {
    const occ = String(p.type || "").toUpperCase().includes("OPTION") ? parseOCC(p.symbol) : null;
    const expiry = occ ? `Exp ${occ.expDate}` : "";
    const val = p.currentValue ? `$${Math.round(p.currentValue).toLocaleString()} value` : "";
    const age = p.openedAt ? `since ${new Date(p.openedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : "";
    const trail = p.trailStop ? "trail stop armed" : "";
    return [expiry, val, age, trail].filter(Boolean).join(" · ") || (occ ? "Options position" : "Equity position");
  };
  const negPositions = portfolio?.positions?.filter(p => (p.dayPnl || 0) < 0).length || 0;
  const greenPositions = (portfolio?.positions?.length || 0) - negPositions;

  // Build Chart.js data from lifeChart API response
  // Derive yesterday close from the second-to-last timeline entry
  const lcYesterdayClose = (() => {
    const tl = lifeChart?.timeline || [];
    if (tl.length >= 2) return tl[tl.length - 2].equity;
    return lifeChart?.yesterday_close ?? lifeChart?.yesterdayClose ?? null;
  })();
  const lifeChartData = (() => {
    // ── Day: return the stable ref — same object every render, no re-init ────
    if (chartRange === "Day") return intradayChartDataRef.current;

    // ── Week / Month / Year: filter historical timeline ───────────────────────
    if (!lifeChart?.timeline?.length) return null;
    const allTl = lifeChart.timeline;
    const fc = lifeChart.forecast || [];
    const events = lifeChart.events || [];
    const today = new Date();

    let tl = allTl;
    if (chartRange === "Week") {
      const cutoff = new Date(today); cutoff.setDate(cutoff.getDate() - 7);
      const cutStr = cutoff.toISOString().slice(0, 10);
      tl = allTl.filter(d => d.date >= cutStr);
      if (!tl.length) tl = allTl.slice(-7);
    } else if (chartRange === "Month") {
      tl = allTl.slice(-30);
    }
    // "Year" = all data

    // Forecast labels — only show on Month/Year, not Week
    const showForecast = chartRange !== "Week";
    const tlLabels = tl.map(d => new Date(d.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }));
    const fcLabels = showForecast ? fc.map(f => {
      const dt = new Date(today); dt.setDate(dt.getDate() + (f.day || 1));
      return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }) : [];
    const labels = [...tlLabels, ...fcLabels];

    // Event dots on the filtered timeline
    const dotColors = ["#fb923c", "#60a5fa", "#4ade80", "#a78bfa", "#f87171", "#fbbf24"];
    const pointRadii = tl.map(() => 0);
    const pointBgColors = tl.map(() => "transparent");
    const pointBorderColors = tl.map(() => "transparent");
    events.forEach((ev, ei) => {
      const idx = tl.findIndex(t => (t.date || "").slice(0, 10) === (ev.date || "").slice(0, 10));
      if (idx >= 0) {
        pointRadii[idx] = 7;
        pointBgColors[idx] = ev.color || dotColors[ei % dotColors.length];
        pointBorderColors[idx] = "white";
      }
    });

    const equityData = [...tl.map(d => d.equity), ...fcLabels.map(() => null)];
    const forecastData = showForecast
      ? [...tl.map(() => null), ...fc.map(d => d.equity)]
      : [];
    if (showForecast && tl.length > 0 && fc.length > 0) forecastData[tl.length - 1] = tl[tl.length - 1].equity;

    const datasets = [
      {
        label: "Equity", data: equityData,
        borderColor: "#22c55e", backgroundColor: "rgba(34,197,94,0.13)", fill: true,
        pointRadius: [...pointRadii, ...fcLabels.map(() => 0)],
        pointBackgroundColor: [...pointBgColors, ...fcLabels.map(() => "transparent")],
        pointBorderColor: [...pointBorderColors, ...fcLabels.map(() => "transparent")],
        pointBorderWidth: 2, pointHoverRadius: 10, pointHoverBorderWidth: 2, borderWidth: 2.5, tension: 0.4,
      },
    ];
    if (showForecast) {
      datasets.push({
        label: "Zi Forecast", data: forecastData,
        borderColor: "#818cf8", borderDash: [6, 4],
        backgroundColor: "rgba(129,140,248,0.05)", fill: true,
        pointRadius: 0, borderWidth: 1.5, tension: 0.4,
      });
    }
    if (lcYesterdayClose) {
      datasets.push({
        label: "Yesterday Close", data: labels.map(() => lcYesterdayClose),
        borderColor: "rgba(251,191,36,0.6)", borderDash: [5, 4],
        pointRadius: 0, borderWidth: 1.2, tension: 0, fill: false,
      });
    }

    // ── Omega ZI AI Autopilot Line ────────────────────────────────────────────
    if (showOmegaAiLine && omegaAiLine?.omega_autopilot_line?.length > 0) {
      const aiPoints = omegaAiLine.omega_autopilot_line;
      const currentEq = tl.length > 0 ? tl[tl.length - 1].equity : (equity || 1000);
      const aiStart = currentEq;
      const aiEnd = aiPoints[aiPoints.length - 1]?.omega_autopilot_equity || aiStart;
      const aiScale = aiEnd > 0 ? aiStart / (aiPoints[0]?.omega_autopilot_equity || aiStart) : 1;

      // Map AI points onto the label positions (project forward from last real point)
      const fcCount = fcLabels.length || Math.max(7, Math.round(aiPoints.length * 0.4));
      const alignedAi = aiPoints.slice(0, fcCount);

      const aiLineData = [
        ...tl.map(() => null),
        ...alignedAi.map(p => p.omega_autopilot_equity * aiScale),
      ].slice(0, labels.length);
      const aiHighData = [
        ...tl.map(() => null),
        ...alignedAi.map(p => p.omega_high_band * aiScale),
      ].slice(0, labels.length);
      const aiLowData = [
        ...tl.map(() => null),
        ...alignedAi.map(p => p.omega_low_band * aiScale),
      ].slice(0, labels.length);

      // Stitch from last real point so line starts connected
      if (tl.length > 0) {
        if (aiLineData[tl.length - 1] === null) aiLineData[tl.length - 1] = currentEq;
        if (aiHighData[tl.length - 1] === null) aiHighData[tl.length - 1] = currentEq;
        if (aiLowData[tl.length - 1] === null) aiLowData[tl.length - 1] = currentEq;
      }

      // Confidence band (fill between high and low)
      datasets.push({
        label: "Ω High Band",
        data: aiHighData,
        borderColor: "transparent",
        backgroundColor: "rgba(167,139,250,0.10)",
        pointRadius: 0, borderWidth: 0, tension: 0.4, fill: "+1",
      });
      datasets.push({
        label: "Ω Low Band",
        data: aiLowData,
        borderColor: "transparent",
        backgroundColor: "rgba(167,139,250,0.10)",
        pointRadius: 0, borderWidth: 0, tension: 0.4, fill: false,
      });
      // Main Omega ZI AI line
      datasets.push({
        label: "Ω Omega ZI AI",
        data: aiLineData,
        borderColor: "#a78bfa",
        backgroundColor: "transparent",
        borderDash: [4, 3],
        pointRadius: 0, borderWidth: 2, tension: 0.4, fill: false,
      });
    }
    // ─────────────────────────────────────────────────────────────────────────

    return { labels, datasets };
  })();

  // Keep a ref so the stable plugin below can read it without closing over state
  const yclRef = useRef(null);
  yclRef.current = lcYesterdayClose;

  const yesterdayLabelPlugin = useMemo(() => ({
    id: "ycLabel",
    afterDraw(chart) {
      const ycl = yclRef.current;
      if (!ycl) return;
      const { ctx, chartArea, scales } = chart;
      if (!scales?.y) return;
      const y = scales.y.getPixelForValue(ycl);
      if (y < chartArea.top || y > chartArea.bottom) return;
      ctx.save();
      ctx.font = "700 9px 'Outfit',sans-serif";
      ctx.fillStyle = "rgba(251,191,36,0.85)";
      ctx.textAlign = "right";
      ctx.fillText("Yesterday close", chartArea.right - 6, y - 5);
      ctx.restore();
    },
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Stable plugin — same object every render; reads live values from ref so Chart.js
  // never sees a new plugin reference and never destroys/re-creates the chart.
  const endpointPlugin = useMemo(() => ({
    id: "lcEndpoint",
    afterDraw(chart) {
      const { ctx, chartArea } = chart;
      const meta0 = chart.getDatasetMeta(0);
      const data0 = chart.data.datasets[0]?.data || [];
      let lastIdx = -1;
      for (let i = data0.length - 1; i >= 0; i--) { if (data0[i] != null) { lastIdx = i; break; } }
      if (lastIdx < 0 || !meta0.data?.[lastIdx]) return;
      const pt = meta0.data[lastIdx];
      const val = data0[lastIdx];
      const { val: pnlV, pct: pnlP } = livePnlRef.current;
      const dotColor = pnlV >= 0 ? "#22c55e" : "#f87171";

      ctx.save();
      // Endpoint dot
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, 6, 0, Math.PI * 2);
      ctx.fillStyle = dotColor;
      ctx.fill();
      ctx.strokeStyle = "white";
      ctx.lineWidth = 2.5;
      ctx.stroke();

      // Equity value label
      const valLabel = val >= 1000 ? `$${(val / 1000).toFixed(2)}k` : `$${val.toFixed(2)}`;
      ctx.font = "700 12px 'Outfit',sans-serif";
      ctx.fillStyle = dotColor;
      ctx.textAlign = "center";
      const aboveRoom = pt.y - chartArea.top;
      if (aboveRoom < 28) {
        ctx.textBaseline = "top";
        ctx.fillText(valLabel, pt.x, pt.y + 10);
      } else {
        ctx.textBaseline = "bottom";
        ctx.fillText(valLabel, pt.x, pt.y - 10);
      }

      // Live P&L bubble
      if (pnlV !== 0 || pnlP !== 0) {
        const sign = pnlV >= 0 ? "+" : "−";
        const absDollar = Math.abs(pnlV);
        const dollarStr = absDollar >= 1000 ? `$${(absDollar / 1000).toFixed(2)}k` : `$${absDollar.toFixed(2)}`;
        const bubbleText = `${sign}${dollarStr}  ${pnlP >= 0 ? "+" : ""}${pnlP.toFixed(2)}%`;
        ctx.font = "700 11px 'Outfit',sans-serif";
        const tw = ctx.measureText(bubbleText).width;
        const bw = tw + 16, bh = 22, br = 8;
        const bx = pt.x + 14 + bw > chartArea.right ? pt.x - 14 - bw : pt.x + 14;
        const by = pt.y - bh / 2;
        ctx.beginPath();
        if (typeof ctx.roundRect === "function") {
          ctx.roundRect(bx, by, bw, bh, br);
        } else {
          ctx.rect(bx, by, bw, bh);
        }
        ctx.fillStyle = pnlV >= 0 ? "rgba(34,197,94,0.15)" : "rgba(248,113,113,0.15)";
        ctx.fill();
        ctx.strokeStyle = pnlV >= 0 ? "rgba(34,197,94,0.45)" : "rgba(248,113,113,0.45)";
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.fillStyle = dotColor;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(bubbleText, bx + bw / 2, by + bh / 2);
      }
      ctx.restore();
    },
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  const lifeChartOpts = {
    responsive: true, maintainAspectRatio: false,
    animation: chartRange === "Day" ? false : { duration: 400 },
    layout: { padding: { left: 0, right: 0, top: 22, bottom: 0 } },
    onClick: (event, elements) => {
      if (!elements?.length) return;
      const el = elements[0];
      if (el.datasetIndex !== 0) return;
      const ptIdx = el.index;
      const allTl = lifeChart?.timeline || [];
      const evts = lifeChart?.events || [];
      // Must reproduce the exact same slicing lifeChartData used to build the
      // rendered chart (below) — ptIdx is an index into THAT sliced array, not
      // the full timeline. Using the full array here made every dot's computed
      // index off by however many entries got sliced off the front, so clicks
      // never matched any event once the real history grew past 30/7 days.
      let tl = allTl;
      if (chartRange === "Week") {
        const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 7);
        const cutStr = cutoff.toISOString().slice(0, 10);
        tl = allTl.filter(d => d.date >= cutStr);
        if (!tl.length) tl = allTl.slice(-7);
      } else if (chartRange === "Month") {
        tl = allTl.slice(-30);
      }
      let matched = null;
      evts.forEach(ev => {
        const idx = tl.findIndex(t => (t.date || "").slice(0, 10) === (ev.date || "").slice(0, 10));
        if (idx === ptIdx) matched = ev;
      });
      if (!matched) return;
      // Set basic info immediately so panel updates right away
      setEventInsight(null);
      setSelectedMilestone({
        title: matched.description || `${matched.symbol || "Trade"} · ${matched.type || "event"}`,
        description: matched.amount != null
          ? `${matched.amount >= 0 ? "Win" : "Loss"} ${fmt$(Math.abs(matched.amount))} on ${matched.symbol || "position"}${matched.changePct != null ? ` · ${matched.changePct >= 0 ? "+" : ""}${fmtN(matched.changePct, 1)}%` : ""}`
          : (matched.description || "Account event recorded"),
        score: matched.changePct != null ? Math.round(Math.abs(matched.changePct)) : null,
        confidence: null,
        positions: null,
        _raw: matched,
      });
      // Fetch Omega ZI AI insight for this event
      setEventInsightLoading(true);
      axios.post(`${API}/account/event-insight`, {
        symbol: matched.symbol || "",
        amount: matched.amount || 0,
        changePct: matched.changePct || 0,
        type: matched.type || "trade",
        date: matched.date || "",
        description: matched.description || "",
        equity_at: matched.equity_at || 0,
      }).then(r => {
        setEventInsight(r.data?.insight || null);
      }).catch(() => {
        setEventInsight(null);
      }).finally(() => {
        setEventInsightLoading(false);
      });
    },
    onHover: (event, elements) => {
      if (event?.native?.target) {
        event.native.target.style.cursor = elements?.length ? "pointer" : "default";
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    scales: {
      x: {
        ticks: { color: "rgba(100,116,139,0.9)", font: { size: 10, weight: "500" }, maxTicksLimit: 7, padding: 6 },
        grid: { display: false },
        border: { display: false },
      },
      y: {
        position: "left",
        ticks: { color: "rgba(100,116,139,0.9)", font: { size: 10 }, callback: v => v >= 1000 ? `$${(v/1000).toFixed(1)}k` : `$${v.toFixed(0)}`, maxTicksLimit: 5, padding: 8 },
        grid: { color: "rgba(59,130,246,0.1)", drawBorder: false },
        border: { display: false },
      },
    },
    interaction: { mode: "nearest", axis: "x" },
  };

  return (
    <div data-testid="account-tab" style={C.root}>

      {/* ── HERO — text above, life chart below ── */}
      <div style={C.hero}>
        <div style={C.heroGlow} />

        {/* Text row */}
        <div style={{ padding: "28px 28px 22px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 20, position: "relative", zIndex: 1 }}>
          <div>
            <div style={{ ...C.kicker, display: "flex", alignItems: "center", gap: 6 }}>
              {chartRange === "Day"
                ? <><span style={{ width: 5, height: 5, borderRadius: "50%", background: "#22c55e", display: "inline-block", flexShrink: 0, animation: "pulse 1s infinite" }} /> Today Live · Crypto 24/7</>
                : `Account ${chartRange === "Week" ? "Last 7 Days" : chartRange === "Month" ? "Last 30 Days" : "Lifetime"} · Click dots for event details`}
            </div>
            <div style={C.balance}>
              ${Math.floor(equity).toLocaleString()}
              <span style={{ fontSize: "38%", fontWeight: 700, opacity: 0.55, marginLeft: 3 }}>.{(equity % 1).toFixed(2).slice(2)}</span>
              {portfolioStale && (
                <span
                  title="Last balance refresh failed — this number may be out of date"
                  style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b", background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)", borderRadius: 6, padding: "2px 7px", marginLeft: 10, verticalAlign: "middle" }}
                >
                  ⚠ STALE
                </span>
              )}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16 }}>
              <span style={C.chip(pnlPos)}>Today&apos;s P&amp;L {pnlPos ? "+" : "−"}${fmtN(Math.abs(pnlVal), 2)} / {pnlPos ? "+" : ""}{fmtN(pnlPct, 2)}%</span>
              <span style={C.chip(null, true)}>Open Positions {portfolio?.positions?.length || 0}</span>
              {chartRange !== "Day" && <span style={C.chip(totalReturn >= 0)}>Account Performance {totalReturn >= 0 ? "+" : ""}{fmtN(totalReturn, 1)}%</span>}
              <span style={C.chip(null, true)}>Zi Forecast {accountInsight ? "active" : "loading"}</span>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
            <div style={C.rangeBar}>
              {["Day", "Week", "Month", "Year"].map(r => (
                <button key={r} style={C.rangeBtn(chartRange === r)} onClick={() => { setChartRange(r); const h = r === "Day" ? "day" : r === "Week" ? "week" : r === "Year" ? "year" : "month"; loadOmegaAiLine(h); }}>
                  {r === "Day" && chartRange === "Day"
                    ? <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#22c55e", display: "inline-block", animation: "pulse 1s infinite" }} />
                        Live
                      </span>
                    : r}
                </button>
              ))}
            </div>
            {/* Omega ZI AI Line toggle strip */}
            {chartRange !== "Day" && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                <button
                  onClick={() => setShowOmegaAiLine(v => !v)}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "5px 10px", borderRadius: 8, cursor: "pointer",
                    border: `1px solid ${showOmegaAiLine ? "rgba(167,139,250,0.45)" : "var(--border)"}`,
                    background: showOmegaAiLine ? "rgba(167,139,250,0.12)" : "var(--layer2)",
                    transition: "all 0.2s",
                  }}
                >
                  <span style={{ width: 18, height: 2, background: showOmegaAiLine ? "#a78bfa" : "var(--dim)", borderRadius: 1, display: "inline-block", flexShrink: 0 }} />
                  <span style={{ fontSize: 10, fontWeight: 700, color: showOmegaAiLine ? "#a78bfa" : "var(--muted)", whiteSpace: "nowrap" }}>Ω AI Line</span>
                  <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 4, fontWeight: 700,
                    background: showOmegaAiLine ? "rgba(167,139,250,0.2)" : "rgba(100,116,139,0.15)",
                    color: showOmegaAiLine ? "#a78bfa" : "var(--dim)" }}>
                    {showOmegaAiLine ? "ON" : "OFF"}
                  </span>
                </button>
                {showOmegaAiLine && omegaAiLine && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 9, color: "var(--muted)" }}>
                    <span>Exp: <strong style={{ color: (omegaAiLine.omega_expected_return_pct || 0) >= 0 ? "#4ade80" : "#f87171" }}>{(omegaAiLine.omega_expected_return_pct || 0).toFixed(1)}%</strong></span>
                    <span>Best: <strong style={{ color: "#4ade80" }}>{(omegaAiLine.omega_best_case_pct || 0).toFixed(1)}%</strong></span>
                    <span>Worst: <strong style={{ color: "#f87171" }}>{(omegaAiLine.omega_worst_case_pct || 0).toFixed(1)}%</strong></span>
                    {/* Backend already computes and sends dataBasis explaining
                        when this forecast is built on a placeholder win/loss
                        size or a defaulted win rate (not enough real closed
                        trades yet) — it was being fetched and stored but never
                        actually shown, so the compounded forecast read exactly
                        like a real, data-driven projection either way. */}
                    {/placeholder|defaulted/i.test(omegaAiLine.dataBasis || "") && (
                      <span title={omegaAiLine.dataBasis} style={{ color: "#f59e0b", cursor: "help", fontWeight: 700 }}>(est.)</span>
                    )}
                  </div>
                )}
              </div>
            )}
            <div style={C.sideCard}>
              <div style={C.sideCardTitle}>Forecast Engine</div>
              <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.03em", color: "var(--text)", marginBottom: 6, lineHeight: 1.1 }}>Equity + Zi AI</div>
              <div style={C.sideCardBody}>
                {accountInsight?.insight
                  ? accountInsight.insight.slice(0, 130) + (accountInsight.insight.length > 130 ? "…" : "")
                  : "AI forecast loading..."}
              </div>
            </div>
          </div>
        </div>

        {/* Life chart — rendered directly on the blue card background */}
        <div style={{ position: "relative", height: "clamp(300px,32vw,430px)" }} data-testid="perf-chart">

          {/* Right-side stats overlay */}
          {lifeChartData && (
            <div style={{ position: "absolute", top: 12, right: 12, zIndex: 10, background: "var(--layer2,rgba(15,23,42,0.82))", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)", borderRadius: 16, padding: "13px 15px", border: "1px solid var(--border)", minWidth: 180, pointerEvents: "none" }}>
              <div style={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.13em", color: "var(--muted)", marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
                Equity + Zi AI
                {chartRange === "Day" && <span style={{ display: "flex", alignItems: "center", gap: 3, background: "rgba(34,197,94,0.12)", borderRadius: 6, padding: "1px 5px", border: "1px solid rgba(34,197,94,0.25)" }}>
                  <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#22c55e", display: "inline-block", animation: "pulse 1s infinite" }} />
                  <span style={{ fontSize: 7, fontWeight: 800, color: "#22c55e", letterSpacing: "0.08em" }}>LIVE</span>
                </span>}
              </div>
              <div style={{ fontSize: 9, color: "var(--dim)", marginBottom: 10, lineHeight: 1.4 }}>
                {chartRange === "Day"
                  ? <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ width: 6, height: 2, background: "#818cf8", borderRadius: 1, display: "inline-block" }} />
                      Trend line (recent slope) · updates every 5s
                    </span>
                  : "Account lifetime — click dots for event details"}
              </div>
              {[
                { label: "Total Equity", val: equity >= 1000 ? `$${(equity / 1000).toFixed(2)}k` : fmt$(equity), color: "var(--text)" },
                { label: "Open Positions", val: portfolio?.positions?.length || 0, color: "var(--text)" },
                { label: "Today's P&L", val: `${pnlPos ? "+" : "−"}$${fmtN(Math.abs(pnlVal), 2)} / ${pnlPos ? "+" : ""}${fmtN(pnlPct, 2)}%`, color: pnlPos ? "var(--green)" : "var(--red)" },
                ...(chartRange !== "Day" ? [{ label: "Account Performance", val: `${totalReturn >= 0 ? "+" : ""}${fmtN(totalReturn, 1)}%`, color: totalReturn >= 0 ? "var(--green)" : "var(--red)" }] : []),
              ].map(({ label, val, color }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, marginBottom: 5 }}>
                  <span style={{ fontSize: 10, color: "var(--muted)", whiteSpace: "nowrap" }}>{label}</span>
                  <span style={{ fontSize: 11, fontWeight: 800, color, whiteSpace: "nowrap" }}>{val}</span>
                </div>
              ))}
              <div style={{ fontSize: 8, color: "var(--dim)", marginTop: 8, lineHeight: 1.5, borderTop: "1px solid var(--border)", paddingTop: 6 }}>
                {chartRange === "Day" ? "Live equity · — — Zi AI day forecast" : "Equity + Zi AI forecast + event markers"}
              </div>
            </div>
          )}

          {lifeChartData
            ? <Line ref={liveChartRef} data={lifeChartData} options={lifeChartOpts} plugins={chartRange === "Day" ? [endpointPlugin] : [yesterdayLabelPlugin, endpointPlugin]} style={{ height: "100%", display: "block", width: "100%" }} />
            : <div style={{ height: "100%", display: "grid", placeItems: "center", color: "var(--dim)", fontSize: 13 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {chartRange === "Day" && <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e", display: "inline-block", animation: "pulse 1s infinite" }} />}
                  {chartRange === "Day" ? "Sampling live equity…" : "Loading chart…"}
                </span>
              </div>}
        </div>

        {/* Omega Enhancement Ideas — top idea preview (iter 137) */}
        <div style={{ marginTop: 14 }}>
          <OmegaEnhancementsCard variant="preview" dispatch={omegaDispatch} />
        </div>

        {/* Event panel */}
        <div className="acc-event-panel" style={C.eventPanel}>
          <div style={C.eventCard}>
            {/* Header row */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)" }}>Event Detail</div>
              {selectedMilestone && (
                <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 9, fontWeight: 700, color: "var(--purple,#a78bfa)", background: "rgba(167,139,250,0.1)", padding: "3px 8px", borderRadius: 999, border: "1px solid rgba(167,139,250,0.25)" }}>
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--purple,#a78bfa)", display: "inline-block", animation: eventInsightLoading ? "pulse 1s infinite" : "none" }} />
                  Omega ZI AI
                </div>
              )}
            </div>

            {selectedMilestone ? (
              <>
                {/* Event title */}
                <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.02em", color: "var(--text)", marginBottom: 6, lineHeight: 1.2 }}>
                  {selectedMilestone.title || selectedMilestone.label}
                </div>
                {/* Raw description */}
                <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 12 }}>
                  {selectedMilestone.description || "—"}
                </div>
                {/* AI insight block */}
                <div style={{ borderRadius: 14, border: "1px solid rgba(167,139,250,0.2)", background: "rgba(167,139,250,0.06)", padding: "12px 14px", marginBottom: 12, minHeight: 60 }}>
                  {eventInsightLoading ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--muted)", fontSize: 12 }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--purple,#a78bfa)", display: "inline-block", animation: "pulse 1s infinite" }} />
                      Omega ZI AI is analyzing this event…
                    </div>
                  ) : eventInsight ? (
                    <div style={{ fontSize: 12, color: "var(--text)", lineHeight: 1.75 }}>{eventInsight}</div>
                  ) : (
                    <div style={{ fontSize: 11, color: "var(--dim)", lineHeight: 1.6, fontStyle: "italic" }}>
                      Click a dot on the chart to load Omega ZI AI insight for that event.
                    </div>
                  )}
                </div>
                {/* Chips */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {selectedMilestone.score != null && <span style={C.watchChip}>Impact {selectedMilestone.score}%</span>}
                  {selectedMilestone._raw?.symbol && <span style={C.watchChip}>{selectedMilestone._raw.symbol}</span>}
                  {selectedMilestone._raw?.date && <span style={C.watchChip}>{selectedMilestone._raw.date}</span>}
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.02em", color: "var(--text)", marginBottom: 8 }}>
                  {milestones?.[0]?.title || "Account Journey Begins"}
                </div>
                <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.7, marginBottom: 12 }}>
                  {milestones?.[0]?.description || "Equity, open-position contribution, and Zi AI forecast share the same canvas so the top half reads like one performance story."}
                </div>
                <div style={{ borderRadius: 14, border: "1px solid rgba(167,139,250,0.2)", background: "rgba(167,139,250,0.06)", padding: "12px 14px", marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: "var(--dim)", lineHeight: 1.6, fontStyle: "italic" }}>
                    Click any dot on the chart above to load Omega ZI AI analysis for that specific trade event.
                  </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  <span style={C.watchChip}>Meta confidence {fmtN(omegaWatch?.meta_confidence ?? 0.82, 2)}</span>
                  <span style={C.watchChip}>{portfolio?.positions?.length || 0} positions live</span>
                  {omegaWatch?.signalSummary?.bullCount > 0 && <span style={{ ...C.watchChip, color: "#22c55e" }}>{omegaWatch.signalSummary.bullCount} bull</span>}
                  {omegaWatch?.signalSummary?.bearCount > 0 && <span style={{ ...C.watchChip, color: "#f87171" }}>{omegaWatch.signalSummary.bearCount} bear</span>}
                  {omegaWatch?.signalSummary?.avgDte != null && <span style={C.watchChip}>Avg {omegaWatch.signalSummary.avgDte}d DTE</span>}
                  {milestones?.length > 0 && <span style={C.watchChip}>{milestones.length} milestones</span>}
                </div>
              </>
            )}
          </div>

          <div style={C.eventCard}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 10 }}>Account Lifetime</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
              {(() => {
                const trades = lifeChart?.events?.filter(e => e.type === "trade") || [];
                const netPnl = trades.reduce((s, e) => s + (e.amount || 0), 0);
                const forecastBias = lifeChart?.forecast?.length > 0
                  ? ((lifeChart.forecast[lifeChart.forecast.length - 1].equity - equity) / (equity || 1) * 100)
                  : (netPnl > 0 ? (netPnl / (equity || 1) * 100) : 0);
                return [
                  { label: "Total Equity", val: equity >= 1000 ? `$${(equity / 1000).toFixed(2)}k` : fmt$(equity) },
                  { label: "Forecast Bias", val: `${forecastBias >= 0 ? "+" : ""}${fmtN(forecastBias, 1)}%` },
                  { label: "Milestones", val: milestones?.length ?? "—" },
                ].map(m => (
                  <div key={m.label}>
                    <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 6 }}>{m.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "var(--text)" }}>{m.val}</div>
                  </div>
                ));
              })()}
            </div>
            {/* Milestone timeline */}
            {milestones?.length > 0 && (
              <div style={{ maxHeight: 180, overflowY: "auto" }}>
                {milestones.slice(0, 20).map((m, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "7px 0", borderBottom: i < Math.min(milestones.length, 20) - 1 ? "0.5px solid var(--divider)" : "none" }}>
                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: m.color || "#a78bfa", flexShrink: 0, marginTop: 4 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text)", lineHeight: 1.3 }}>{m.title}</div>
                      {m.description && <div style={{ fontSize: 10, color: "var(--dim)", marginTop: 1 }}>{m.description}</div>}
                    </div>
                    <div style={{ fontSize: 9, color: "var(--dim)", flexShrink: 0 }}>
                      {m.ts ? new Date(m.ts).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── OMEGA GOALS ── */}
      <div style={{ padding: "0 20px", marginBottom: 20 }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)" }}>Omega Goals</div>
            {goals.filter(g => g.progress >= 100).length > 0 && (
              <span style={{ fontSize: 9, fontWeight: 800, color: "#22c55e", background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 999, padding: "2px 7px" }}>
                {goals.filter(g => g.progress >= 100).length} REACHED
              </span>
            )}
          </div>
          <button
            onClick={() => setAddGoalOpen(v => !v)}
            style={{ fontSize: 11, fontWeight: 700, color: "var(--purple,#a78bfa)", background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.25)", borderRadius: 20, padding: "5px 12px", cursor: "pointer" }}
          >
            {addGoalOpen ? "✕ Cancel" : "+ Add Goal"}
          </button>
        </div>

        {/* Add Goal inline form */}
        {addGoalOpen && (
          <div style={{ background: "var(--layer1)", border: "1px solid rgba(167,139,250,0.25)", borderRadius: 18, padding: "16px 18px", marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--purple,#a78bfa)", marginBottom: 12, letterSpacing: "0.06em", textTransform: "uppercase" }}>New Goal</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <input
                placeholder="Goal title…"
                value={newGoal.title}
                onChange={e => setNewGoal(p => ({ ...p, title: e.target.value }))}
                style={{ gridColumn: "1/-1", padding: "9px 12px", borderRadius: 10, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)", fontSize: 13, outline: "none" }}
              />
              <select
                value={newGoal.metric}
                onChange={e => setNewGoal(p => ({ ...p, metric: e.target.value }))}
                style={{ padding: "9px 12px", borderRadius: 10, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)", fontSize: 12 }}
              >
                <option value="pnl">P&amp;L ($)</option>
                <option value="equity">Equity ($)</option>
                <option value="winRate">Win Rate (%)</option>
                <option value="trades">Total Trades</option>
              </select>
              <input
                type="number"
                placeholder="Target value"
                value={newGoal.target}
                onChange={e => setNewGoal(p => ({ ...p, target: e.target.value }))}
                style={{ padding: "9px 12px", borderRadius: 10, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)", fontSize: 13, outline: "none" }}
              />
              <select
                value={newGoal.timeframe}
                onChange={e => setNewGoal(p => ({ ...p, timeframe: e.target.value }))}
                style={{ padding: "9px 12px", borderRadius: 10, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)", fontSize: 12 }}
              >
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="quarter">This Quarter</option>
                <option value="year">This Year</option>
              </select>
            </div>
            <button
              onClick={() => { createGoal(); setAddGoalOpen(false); }}
              disabled={!newGoal.title || !newGoal.target}
              style={{ width: "100%", padding: "10px", borderRadius: 12, border: "none", background: newGoal.title && newGoal.target ? "var(--purple,#a78bfa)" : "var(--layer2)", color: newGoal.title && newGoal.target ? "#fff" : "var(--dim)", fontWeight: 700, fontSize: 13, cursor: newGoal.title && newGoal.target ? "pointer" : "default", transition: "background 0.2s" }}
            >
              Set Goal
            </button>
          </div>
        )}

        {/* Goal cards horizontal scroll */}
        {goals.length === 0 ? (
          <div style={{ padding: "24px 0", textAlign: "center", color: "var(--dim)", fontSize: 12, border: "1px dashed var(--border)", borderRadius: 18 }}>
            No goals yet — tap <strong style={{ color: "var(--purple,#a78bfa)" }}>+ Add Goal</strong> to give Omega a target to chase.
          </div>
        ) : (
          <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 4, scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch", msOverflowStyle: "none", scrollbarWidth: "none" }}>
            {goals.map(g => {
              const pct = Math.min(100, g.progress || 0);
              const reached = pct >= 100;
              const metricLabel = { pnl: "P&L", equity: "Equity", winRate: "Win Rate", trades: "Trades" }[g.metric] || g.metric;
              const currentFmt = g.metric === "pnl" || g.metric === "equity" ? fmt$(g.current || 0) : g.metric === "winRate" ? `${fmtN(g.current || 0, 1)}%` : `${g.current || 0}`;
              const targetFmt = g.metric === "pnl" || g.metric === "equity" ? fmt$(g.target) : g.metric === "winRate" ? `${g.target}%` : `${g.target}`;
              const barColor = reached ? "#22c55e" : pct >= 70 ? "#a78bfa" : pct >= 40 ? "#60a5fa" : "var(--muted)";
              return (
                <div
                  key={g.goal_id}
                  onClick={() => setGoalModal(g)}
                  style={{ flexShrink: 0, width: 220, scrollSnapAlign: "start", background: "var(--layer1)", border: `1px solid ${reached ? "rgba(34,197,94,0.35)" : "var(--border)"}`, borderRadius: 20, padding: "16px 18px", cursor: "pointer", transition: "border-color 0.15s, transform 0.15s", position: "relative" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = barColor; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = reached ? "rgba(34,197,94,0.35)" : "var(--border)"; e.currentTarget.style.transform = "none"; }}
                >
                  {reached && (
                    <div style={{ position: "absolute", top: 12, right: 12, fontSize: 9, fontWeight: 900, color: "#22c55e", background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 999, padding: "2px 7px", letterSpacing: "0.08em" }}>REACHED ✓</div>
                  )}
                  <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 5 }}>{metricLabel} · {g.timeframe}</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "var(--text)", lineHeight: 1.3, marginBottom: 10, paddingRight: reached ? 50 : 0 }}>{g.title}</div>

                  {/* Progress bar */}
                  <div style={{ height: 6, borderRadius: 999, background: "var(--layer2)", marginBottom: 8, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, borderRadius: 999, background: barColor, transition: "width 0.6s ease" }} />
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: barColor }}>{pct.toFixed(0)}%</div>
                    <div style={{ fontSize: 10, color: "var(--muted)" }}>{currentFmt} <span style={{ color: "var(--dim)" }}>/ {targetFmt}</span></div>
                  </div>

                  {g.daysToGoal != null && !reached && (
                    <div style={{ marginTop: 8, fontSize: 10, color: "var(--dim)", background: "var(--layer2)", borderRadius: 8, padding: "4px 8px", textAlign: "center" }}>
                      ~{g.daysToGoal}d to goal · +{g.metric === "pnl" || g.metric === "equity" ? fmt$(g.ratePerDay || 0) : `${fmtN(g.ratePerDay || 0, 1)}`}/day
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Goal Detail Modal */}
      {goalModal && (() => {
        const g = goalModal;
        const pct = Math.min(100, g.progress || 0);
        const reached = pct >= 100;
        const metricLabel = { pnl: "P&L", equity: "Equity", winRate: "Win Rate", trades: "Trades" }[g.metric] || g.metric;
        const currentFmt = g.metric === "pnl" || g.metric === "equity" ? fmt$(g.current || 0) : g.metric === "winRate" ? `${fmtN(g.current || 0, 1)}%` : `${g.current || 0}`;
        const targetFmt = g.metric === "pnl" || g.metric === "equity" ? fmt$(g.target) : g.metric === "winRate" ? `${g.target}%` : `${g.target}`;
        const barColor = reached ? "#22c55e" : pct >= 70 ? "#a78bfa" : pct >= 40 ? "#60a5fa" : "#818cf8";
        const strat = goalStrategy[g.goal_id];
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", zIndex: 1200, display: "grid", placeItems: "center" }} onClick={() => setGoalModal(null)}>
            <div style={{ width: "min(480px,94vw)", maxHeight: "85vh", overflowY: "auto", padding: 28, borderRadius: 28, background: "var(--surface)", border: "1px solid var(--border)", boxShadow: "0 32px 90px rgba(0,0,0,0.35)", color: "var(--text)" }} onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 5 }}>
                    {metricLabel} · {g.timeframe}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.2 }}>{g.title}</div>
                </div>
                <button onClick={() => setGoalModal(null)} style={{ background: "var(--layer2)", border: "1px solid var(--border)", color: "var(--text)", width: 32, height: 32, borderRadius: "50%", cursor: "pointer", fontSize: 16, flexShrink: 0 }}>✕</button>
              </div>

              {/* Big progress bar */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                  <div style={{ fontSize: 38, fontWeight: 800, letterSpacing: "-0.04em", color: barColor }}>{pct.toFixed(0)}%</div>
                  <div style={{ fontSize: 13, color: "var(--muted)" }}>{currentFmt} <span style={{ color: "var(--dim)" }}>of {targetFmt}</span></div>
                </div>
                <div style={{ height: 12, borderRadius: 999, background: "var(--layer2)", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${pct}%`, borderRadius: 999, background: `linear-gradient(90deg, ${barColor}99, ${barColor})`, transition: "width 0.8s ease", boxShadow: reached ? `0 0 12px ${barColor}60` : "none" }} />
                </div>
                {reached && <div style={{ marginTop: 8, textAlign: "center", fontSize: 13, fontWeight: 800, color: "#22c55e" }}>Goal Reached! 🎯</div>}
              </div>

              {/* Stats grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
                <div style={{ padding: "12px 14px", borderRadius: 14, background: "var(--layer1)", border: "1px solid var(--border)" }}>
                  <div style={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 5 }}>Current</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "var(--text)" }}>{currentFmt}</div>
                </div>
                <div style={{ padding: "12px 14px", borderRadius: 14, background: "var(--layer1)", border: "1px solid var(--border)" }}>
                  <div style={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 5 }}>Target</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "var(--text)" }}>{targetFmt}</div>
                </div>
                {g.ratePerDay != null && (
                  <div style={{ padding: "12px 14px", borderRadius: 14, background: "var(--layer1)", border: "1px solid var(--border)" }}>
                    <div style={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 5 }}>Daily Rate</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#60a5fa" }}>
                      {g.metric === "pnl" || g.metric === "equity" ? fmt$(g.ratePerDay) : `${fmtN(g.ratePerDay, 2)}`}/day
                    </div>
                  </div>
                )}
                {g.daysToGoal != null && !reached && (
                  <div style={{ padding: "12px 14px", borderRadius: 14, background: "var(--layer1)", border: "1px solid var(--border)" }}>
                    <div style={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 5 }}>ETA</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: g.daysToGoal <= 7 ? "#22c55e" : g.daysToGoal <= 30 ? "#a78bfa" : "var(--muted)" }}>
                      {g.daysToGoal === 999 ? "—" : `~${g.daysToGoal}d`}
                    </div>
                  </div>
                )}
              </div>

              {/* Omega AI Strategy */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--purple,#a78bfa)" }}>Omega AI Strategy</div>
                  {!strat?.text && (
                    <button
                      onClick={() => fetchGoalStrategy(g.goal_id)}
                      disabled={strat?.loading}
                      style={{ fontSize: 11, fontWeight: 700, color: "#fff", background: "var(--purple,#a78bfa)", border: "none", borderRadius: 16, padding: "6px 14px", cursor: strat?.loading ? "default" : "pointer", opacity: strat?.loading ? 0.7 : 1 }}
                    >
                      {strat?.loading ? "Asking Omega…" : "Ask Omega"}
                    </button>
                  )}
                </div>
                <div style={{ borderRadius: 16, border: "1px solid rgba(167,139,250,0.2)", background: "rgba(167,139,250,0.06)", padding: "14px 16px", minHeight: 64 }}>
                  {strat?.loading ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--muted)", fontSize: 12 }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--purple,#a78bfa)", display: "inline-block", animation: "pulse 1s infinite" }} />
                      Omega ZI AI is building your strategy…
                    </div>
                  ) : strat?.text ? (
                    <div style={{ fontSize: 12, color: "var(--text)", lineHeight: 1.75, whiteSpace: "pre-wrap" }}>{strat.text}</div>
                  ) : (
                    <div style={{ fontSize: 11, color: "var(--dim)", fontStyle: "italic", lineHeight: 1.6 }}>
                      Tap "Ask Omega" and Omega ZI AI will build a step-by-step plan to hit this goal — weekly scans, risk budget, optimal Zi scores, and a projected timeline.
                    </div>
                  )}
                </div>
              </div>

              {/* Delete */}
              <button
                onClick={() => { deleteGoal(g.goal_id); setGoalModal(null); }}
                style={{ width: "100%", padding: "10px", borderRadius: 14, border: "1px solid rgba(248,113,113,0.3)", background: "rgba(248,113,113,0.06)", color: "#f87171", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
              >
                Delete Goal
              </button>
            </div>
          </div>
        );
      })()}

      {/* ── LOWER 3-COLUMN GRID ── */}
      <div className="acc-lower" style={C.lower}>

        {/* Col 1: Omega AI Watch Room */}
        <div style={C.secCard}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <div style={C.secTitle}>Omega AI Watch Room</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {marketStatus && (() => {
                const s = marketStatus.session;
                const isOpen = s === "regular";
                const isPre = s === "premarket";
                const isAfter = s === "afterHours";
                const dotColor = isOpen ? "#22c55e" : isPre ? "#fbbf24" : isAfter ? "#818cf8" : "#6b7280";
                const label = isOpen ? "OPEN" : isPre ? "PRE" : isAfter ? "AH" : "CLOSED";
                const bg = isOpen ? "rgba(34,197,94,0.1)" : isPre ? "rgba(251,191,36,0.1)" : isAfter ? "rgba(129,140,248,0.1)" : "rgba(107,114,128,0.1)";
                const border = isOpen ? "rgba(34,197,94,0.25)" : isPre ? "rgba(251,191,36,0.25)" : isAfter ? "rgba(129,140,248,0.25)" : "rgba(107,114,128,0.2)";
                return (
                  <span title={marketStatus.etTime + " · Next: " + marketStatus.nextOpen} style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: dotColor, background: bg, border: `1px solid ${border}`, borderRadius: 999, padding: "3px 8px", display: "flex", alignItems: "center", gap: 4, cursor: "default" }}>
                    <span style={{ width: 5, height: 5, borderRadius: "50%", background: dotColor, display: "inline-block", animation: isOpen ? "pulse 1s infinite" : "none" }} />
                    {label}
                  </span>
                );
              })()}
              <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#22c55e", background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)", borderRadius: 999, padding: "3px 8px", display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#22c55e", display: "inline-block", animation: "pulse 1s infinite" }} />
                LIVE
              </span>
            </div>
          </div>
          <div style={C.secSub}>Click any position for full analytics + live chart</div>

          {omegaWatch?.positions?.length > 0 ? (
            omegaWatch.positions.map((pos, i) => {
              const sym = pos.ticker || pos.symbol;
              const action = pos.action || "HOLD";
              const actionColor = WATCH_ACTION_COLORS[action] || "#818cf8";
              const actionBg = `${actionColor}14`;
              const pct = pos.dayGainPct ?? pos.gainPct ?? 0;
              const pctPos = pct >= 0;
              return (
                <div key={i} onClick={() => setWatchRoomModal(pos)}
                  style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 10, alignItems: "center", padding: "12px 14px", borderRadius: 16, border: "1px solid var(--border)", background: "var(--layer1)", marginBottom: 8, cursor: "pointer", transition: "border-color 0.15s, background 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = actionColor; e.currentTarget.style.background = actionBg; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "var(--layer1)"; }}
                >
                  {/* Type badge */}
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: actionBg, border: `1px solid ${actionColor}30`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <span style={{ fontSize: 9, fontWeight: 900, color: actionColor, letterSpacing: "0.05em" }}>{pos.type === "OPTION" ? (pos.side === "Put" ? "PUT" : "CALL") : pos.type === "CRYPTO" ? "CRY" : "STK"}</span>
                  </div>
                  {/* Info */}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text)", marginBottom: 2, display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                      {pos.display || sym}
                      {pos.type === "OPTION" && pos.strike && <span style={{ fontSize: 10, color: "var(--muted)", fontWeight: 500 }}>${pos.strike} · {pos.expDate}</span>}
                      {pos.teaScore != null && <span style={{ fontSize: 8, fontWeight: 700, padding: "1px 5px", borderRadius: 4, background: pos.teaScore >= 70 ? "rgba(74,222,128,0.15)" : pos.teaScore >= 40 ? "rgba(250,204,21,0.15)" : "rgba(239,68,68,0.15)", color: pos.teaScore >= 70 ? "#4ade80" : pos.teaScore >= 40 ? "#fbbf24" : "#f87171" }}>TEA {pos.teaScore}</span>}
                      {pos.ziSignal?.bias && <span style={{ fontSize: 8, fontWeight: 700, padding: "1px 5px", borderRadius: 4, background: pos.ziSignal.bias === "BULLISH" ? "rgba(34,197,94,0.12)" : pos.ziSignal.bias === "BEARISH" ? "rgba(248,113,113,0.12)" : "rgba(167,139,250,0.12)", color: pos.ziSignal.bias === "BULLISH" ? "#22c55e" : pos.ziSignal.bias === "BEARISH" ? "#f87171" : "#a78bfa" }}>Ω {pos.ziSignal.bias}</span>}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--muted)", lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {pos.signals?.[0] || `${sym} · ${pos.type}`}
                    </div>
                    {/* Greeks row — options only */}
                    {pos.type === "OPTION" && pos.greeks && Object.keys(pos.greeks).length > 0 && (
                      <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
                        {pos.greeks.delta != null && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, background: "rgba(96,165,250,0.1)", color: "#60a5fa", fontWeight: 700 }}>Δ {fmtN(pos.greeks.delta, 3)}</span>}
                        {pos.greeks.theta != null && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, background: "rgba(248,113,113,0.1)", color: "#f87171", fontWeight: 700 }}>Θ ${fmtN(Math.abs(pos.greeks.theta || 0) * 100, 2)}/d</span>}
                        {pos.greeks.iv != null && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, background: "rgba(251,191,36,0.1)", color: "#fbbf24", fontWeight: 700 }}>IV {fmtN((pos.greeks.iv || 0) * 100, 0)}%</span>}
                        {pos.type === "OPTION" && pos.itm != null && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, background: pos.itm ? "rgba(34,197,94,0.1)" : "rgba(107,114,128,0.1)", color: pos.itm ? "#22c55e" : "var(--muted)", fontWeight: 700 }}>{pos.itm ? "ITM" : "OTM"} {Math.abs(pos.moneyness || 0).toFixed(1)}%</span>}
                      </div>
                    )}
                  </div>
                  {/* Right: pct + action + trail chip */}
                  <div style={{ flexShrink: 0, textAlign: "right" }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: pctPos ? "var(--green)" : "var(--red)", marginBottom: 3 }}>{pctPos ? "+" : ""}{fmtN(pct, 1)}%</div>
                    <div style={{ fontSize: 9, fontWeight: 800, color: actionColor, background: actionBg, border: `1px solid ${actionColor}30`, borderRadius: 6, padding: "2px 6px", marginBottom: 3 }}>{action}</div>
                    {pos.trailStop && <div style={{ fontSize: 8, fontWeight: 800, color: "#4ade80", background: "rgba(74,222,128,0.12)", border: "1px solid rgba(74,222,128,0.3)", borderRadius: 4, padding: "1px 5px", marginBottom: 2 }}>TRAIL ✓</div>}
                    {pos.dte != null && pos.type === "OPTION" && <div style={{ fontSize: 8, color: pos.dte <= 7 ? "#f87171" : "var(--dim)" }}>{pos.dte}d left</div>}
                  </div>
                </div>
              );
            })
          ) : (
            <div style={{ padding: "32px 0", textAlign: "center", color: "var(--dim)", fontSize: 12 }}>
              {omegaWatch === null
                ? <><span style={{ display: "block", marginBottom: 8, animation: "pulse 1s infinite" }}>⬤</span>Loading watch room...</>
                : "No active signals right now"}
            </div>
          )}
        </div>

        {/* Watch Room Popup Modal */}
        {watchRoomModal && <WatchRoomModal pos={watchRoomModal} onClose={() => setWatchRoomModal(null)} onQuickTrade={(p, mode) => { setWatchRoomModal(null); setQuickTrade({ position: p, mode }); }} onTrailStop={(p) => { setWatchRoomModal(null); setTrailStop({ position: p }); }} />}

        {/* Col 2: Open Positions */}
        <div style={C.secCard}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <div style={C.secTitle}>Open Positions</div>
            {portfolio?.positions?.length > 0 && (
              <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#22c55e", background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)", borderRadius: 999, padding: "3px 8px", display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#22c55e", display: "inline-block", animation: "pulse 1s infinite" }} />LIVE
              </span>
            )}
          </div>
          <div style={C.secSub}>Click any position for live P&L forecast + analytics</div>

          {portfolio?.positions?.length > 0 && (
            <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
              {greenPositions > 0 && <span style={{ ...C.chip(true), fontSize: 11 }}>{greenPositions} green today</span>}
              {negPositions > 0 && <span style={{ ...C.chip(false), fontSize: 11 }}>{negPositions} on risk watch</span>}
            </div>
          )}

          {portfolio?.positions?.length > 0 ? (
            portfolio.positions.map((p, i) => {
              const neg = (p.dayGainValue ?? p.dayPnl ?? 0) < 0;
              const isOpt = String(p.type || "").toUpperCase().includes("OPTION");
              const isCry = String(p.type || "").toUpperCase().includes("CRYPTO");
              const occMatch = isOpt ? /^[A-Z]+\d{6}([CP])\d{8}$/.exec(p.symbol || "") : null;
              const isPut = p.side === "Put" || p.optionType === "put" || p.optionType === "PUT" || p.putCall === "put" || occMatch?.[1] === "P";
              const typeLabel = isOpt ? (isPut ? "PUT" : "CALL") : isCry ? "CRY" : "STK";
              const typeColor = isOpt ? "#a78bfa" : isCry ? "#f59e0b" : "#60a5fa";
              // Merge omega watch enrichment by symbol
              const ow = omegaWatch?.positions?.find(w => w.symbol === p.symbol);
              return (
                <div key={i}
                  onClick={() => setOpenPosition(p)}
                  style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 10, alignItems: "center", padding: "12px 14px", borderRadius: 16, border: "1px solid var(--border)", background: "var(--layer1)", marginBottom: 8, cursor: "pointer", transition: "border-color 0.15s, background 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = neg ? "rgba(248,113,113,0.4)" : "rgba(34,197,94,0.4)"; e.currentTarget.style.background = neg ? "rgba(248,113,113,0.04)" : "rgba(34,197,94,0.04)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "var(--layer1)"; }}
                >
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: `${typeColor}15`, border: `1px solid ${typeColor}30`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <span style={{ fontSize: 9, fontWeight: 900, color: typeColor, letterSpacing: "0.05em" }}>{typeLabel}</span>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text)", marginBottom: 2, display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                      {posLabel(p)}
                      {p.isAutopilot && <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: "rgba(124,58,237,0.15)", color: "#a78bfa", fontWeight: 800 }}>AI</span>}
                      {ow?.teaScore != null && <span style={{ fontSize: 8, fontWeight: 700, padding: "1px 5px", borderRadius: 4, background: ow.teaScore >= 70 ? "rgba(74,222,128,0.15)" : ow.teaScore >= 40 ? "rgba(250,204,21,0.15)" : "rgba(239,68,68,0.15)", color: ow.teaScore >= 70 ? "#4ade80" : ow.teaScore >= 40 ? "#fbbf24" : "#f87171" }}>TEA {ow.teaScore}</span>}
                      {ow?.ziSignal?.bias && <span style={{ fontSize: 8, fontWeight: 700, padding: "1px 5px", borderRadius: 4, background: ow.ziSignal.bias === "BULLISH" ? "rgba(34,197,94,0.12)" : ow.ziSignal.bias === "BEARISH" ? "rgba(248,113,113,0.12)" : "rgba(167,139,250,0.12)", color: ow.ziSignal.bias === "BULLISH" ? "#22c55e" : ow.ziSignal.bias === "BEARISH" ? "#f87171" : "#a78bfa" }}>Ω {ow.ziSignal.bias}</span>}
                      {ow?.action && ow.action !== "HOLD" && <span style={{ fontSize: 8, fontWeight: 800, padding: "1px 5px", borderRadius: 4, background: `${WATCH_ACTION_COLORS[ow.action] || "#818cf8"}18`, color: WATCH_ACTION_COLORS[ow.action] || "#818cf8" }}>{ow.action}</span>}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{posSubLabel(p)}</div>
                    {/* Greeks — from omega watch enrichment */}
                    {isOpt && ow?.greeks && Object.keys(ow.greeks).length > 0 && (
                      <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
                        {ow.greeks.delta != null && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, background: "rgba(96,165,250,0.1)", color: "#60a5fa", fontWeight: 700 }}>Δ {fmtN(ow.greeks.delta, 3)}</span>}
                        {ow.greeks.theta != null && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, background: "rgba(248,113,113,0.1)", color: "#f87171", fontWeight: 700 }}>Θ ${fmtN(Math.abs(ow.greeks.theta || 0) * 100, 2)}/d</span>}
                        {ow.greeks.iv != null && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, background: "rgba(251,191,36,0.1)", color: "#fbbf24", fontWeight: 700 }}>IV {fmtN((ow.greeks.iv || 0) * 100, 0)}%</span>}
                        {ow.itm != null && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 3, background: ow.itm ? "rgba(34,197,94,0.1)" : "rgba(107,114,128,0.1)", color: ow.itm ? "#22c55e" : "var(--muted)", fontWeight: 700 }}>{ow.itm ? "ITM" : "OTM"} {Math.abs(ow.moneyness || 0).toFixed(1)}%</span>}
                      </div>
                    )}
                  </div>
                  <div style={{ flexShrink: 0, textAlign: "right" }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: neg ? "var(--red)" : "var(--green)" }}>{neg ? "−" : "+"}${fmtN(Math.abs(p.dayGainValue ?? p.dayPnl ?? 0), 2)}</div>
                    <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>today</div>
                    {ow?.trailStop && <div style={{ fontSize: 8, fontWeight: 800, color: "#4ade80", background: "rgba(74,222,128,0.12)", border: "1px solid rgba(74,222,128,0.3)", borderRadius: 4, padding: "1px 5px", marginTop: 2 }}>TRAIL ✓</div>}
                    {ow?.dte != null && isOpt && <div style={{ fontSize: 8, color: ow.dte <= 7 ? "#f87171" : "var(--dim)", marginTop: 1 }}>{ow.dte}d left</div>}
                  </div>
                </div>
              );
            })
          ) : (
            <div style={{ padding: "30px 0", textAlign: "center", color: "var(--dim)", fontSize: 12 }}>
              {portfolio ? "No open positions" : "Loading positions..."}
            </div>
          )}

          {zbucksPositions.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#fbbf24", marginBottom: 8 }}>
                Your zBucks Positions (paper)
              </div>
              {zbucksPositions.map((p, i) => {
                const neg = (p.gainPct ?? 0) < 0;
                return (
                  <div key={p.lockId || i}
                    onClick={() => setQuickTrade({ position: p, mode: "sell" })}
                    style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "center", padding: "10px 14px", borderRadius: 14, border: "1px solid rgba(251,191,36,0.3)", background: "rgba(251,191,36,0.05)", marginBottom: 8, cursor: "pointer" }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 800, color: "var(--text)" }}>
                        {p.ticker}{p.strike ? ` $${p.strike} ${String(p.optionType || "").toUpperCase()}` : ""}
                      </div>
                      <div style={{ fontSize: 10, color: "var(--muted)" }}>
                        {p.quantity} @ ${fmtN(p.avgCost, p.optionType ? 2 : 4)} → ${fmtN(p.lastPrice, p.optionType ? 2 : 4)}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: neg ? "var(--red)" : "var(--green)" }}>
                        {neg ? "" : "+"}{fmtN(p.gainPct, 1)}%
                      </div>
                      <div style={{ fontSize: 9, color: "var(--muted)" }}>tap to sell</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </div>

        {/* Open Position Popup Modal */}
        {openPosition && <OpenPositionModal position={openPosition} onClose={() => setOpenPosition(null)} />}

        {/* Col 3: Equity Breakdown */}
        <div style={C.secCard}>
          <div style={C.secTitle}>Equity Breakdown</div>
          <div style={C.secSub}>Allocation and source of account performance.</div>

          {portfolio?.equity?.length > 0 ? (() => {
            const eqColors = ["var(--blue)", "var(--green)", "var(--yellow)", "var(--purple)", "var(--red)"];
            const eqHex    = ["#60a5fa", "#4ade80", "#fbbf24", "#a78bfa", "#f87171"];
            const eqDescs  = [
              "High-conviction contract positions driving most of today's lift.",
              "Dry powder and base equity allocation available for new setups.",
              "Managed capital running through filtered autonomous suggestions.",
              "Held back for event days, spreads, and recovery protection.",
              "Remaining allocation.",
            ];
            const total = portfolio.equity.reduce((s, e) => s + (e.value || 0), 0);
            return (
              <>
                <div style={{ display: "grid", gap: 10, marginBottom: 18 }}>
                  {portfolio.equity.map((e, i) => {
                    const pct = total > 0 ? ((e.value / total) * 100) : 0;
                    return (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, padding: "14px 16px", borderRadius: 16, border: "1px solid var(--border)", background: "var(--layer1)" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>{e.type}</div>
                          <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.5 }}>{eqDescs[i] || `${fmt$(e.value)} · ${pct.toFixed(1)}% of portfolio`}</div>
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: eqColors[i % eqColors.length], flexShrink: 0 }}>{pct.toFixed(0)}%</div>
                      </div>
                    );
                  })}
                </div>
                <div style={C.brkBar}>
                  {portfolio.equity.map((e, i) => (
                    <div key={i} style={{ flex: total > 0 ? e.value / total : 1, background: eqHex[i % eqHex.length], transition: "flex 0.5s" }} />
                  ))}
                </div>
              </>
            );
          })() : (
            <div style={{ padding: "30px 0", textAlign: "center", color: "var(--dim)", fontSize: 12 }}>
              {portfolio ? "No equity data" : "Loading breakdown..."}
            </div>
          )}
        </div>
      </div>


            {/* ── MODALS ── */}
      {metricModal && metricModal !== "pnl" && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 1000, display: "grid", placeItems: "center" }} onClick={() => setMetricModal(null)}>
          <div style={{ width: "min(420px,92vw)", padding: 28, borderRadius: 24, background: "var(--surface)", border: "1px solid var(--border)", boxShadow: "0 30px 80px rgba(0,0,0,0.3)", color: "var(--text)" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 6 }}>
                  {metricModal === "equity" ? "Total Equity" : metricModal === "bp" ? "Buying Power" : "Options Buying Power"}
                </div>
                <div style={{ fontSize: 38, fontWeight: 800, letterSpacing: "-0.04em", color: "var(--text)" }}>
                  {metricModal === "equity" ? fmt$(portfolio?.totalEquity) : metricModal === "bp" ? fmt$(portfolio?.buyingPower) : fmt$(portfolio?.optionsBuyingPower)}
                </div>
              </div>
              <button onClick={() => setMetricModal(null)} style={{ background: "var(--layer2)", border: "1px solid var(--border)", color: "var(--text)", width: 32, height: 32, borderRadius: "50%", cursor: "pointer", fontSize: 16 }}>✕</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
              {metricModal === "equity" && (<>
                <div style={{ padding: "12px 14px", borderRadius: 14, background: "var(--layer2)", border: "1px solid var(--border)" }}><div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 6 }}>Options Value</div><div style={{ fontSize: 15, fontWeight: 700, color: "var(--blue)" }}>{fmt$(portfolio?.equity?.find(e => e.type?.includes("OPTION"))?.value)}</div></div>
                <div style={{ padding: "12px 14px", borderRadius: 14, background: "var(--layer2)", border: "1px solid var(--border)" }}><div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 6 }}>Equity Value</div><div style={{ fontSize: 15, fontWeight: 700, color: "var(--green)" }}>{fmt$(portfolio?.equity?.find(e => e.type?.includes("EQUITY"))?.value)}</div></div>
                <div style={{ padding: "12px 14px", borderRadius: 14, background: "var(--layer2)", border: "1px solid var(--border)" }}><div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 6 }}>Buying Power</div><div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>{fmt$(portfolio?.buyingPower)}</div></div>
                <div style={{ padding: "12px 14px", borderRadius: 14, background: "var(--layer2)", border: "1px solid var(--border)" }}><div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 6 }}>Cash BP</div><div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>{fmt$(portfolio?.cashBuyingPower)}</div></div>
              </>)}
              {metricModal === "bp" && (<>
                <div style={{ padding: "12px 14px", borderRadius: 14, background: "var(--layer2)", border: "1px solid var(--border)" }}><div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 6 }}>Options BP</div><div style={{ fontSize: 15, fontWeight: 700, color: "var(--purple)" }}>{fmt$(portfolio?.optionsBuyingPower)}</div></div>
                <div style={{ padding: "12px 14px", borderRadius: 14, background: "var(--layer2)", border: "1px solid var(--border)" }}><div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 6 }}>Cash BP</div><div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>{fmt$(portfolio?.cashBuyingPower)}</div></div>
                <div style={{ padding: "12px 14px", borderRadius: 14, background: "var(--layer2)", border: "1px solid var(--border)" }}><div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 6 }}>Utilization</div><div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>{portfolio?.totalEquity > 0 ? `${(100 - (portfolio.buyingPower / portfolio.totalEquity * 100)).toFixed(1)}%` : "—"}</div></div>
                <div style={{ padding: "12px 14px", borderRadius: 14, background: "var(--layer2)", border: "1px solid var(--border)" }}><div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 6 }}>Open Positions</div><div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>{portfolio?.positions?.length || 0}</div></div>
              </>)}
              {metricModal === "obp" && (<>
                <div style={{ padding: "12px 14px", borderRadius: 14, background: "var(--layer2)", border: "1px solid var(--border)" }}><div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 6 }}>Total BP</div><div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>{fmt$(portfolio?.buyingPower)}</div></div>
                <div style={{ padding: "12px 14px", borderRadius: 14, background: "var(--layer2)", border: "1px solid var(--border)" }}><div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 6 }}>Cash BP</div><div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>{fmt$(portfolio?.cashBuyingPower)}</div></div>
                <div style={{ padding: "12px 14px", borderRadius: 14, background: "var(--layer2)", border: "1px solid var(--border)" }}><div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 6 }}>Max Slots</div><div style={{ fontSize: 15, fontWeight: 700, color: "var(--blue)" }}>{omegaStatus?.capitalTier?.max_positions ?? "—"}</div></div>
                <div style={{ padding: "12px 14px", borderRadius: 14, background: "var(--layer2)", border: "1px solid var(--border)" }}><div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 6 }}>Risk / Trade</div><div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>{omegaStatus?.capitalTier?.max_risk_pct ? `${omegaStatus.capitalTier.max_risk_pct}%` : "—"}</div></div>
              </>)}
            </div>
            <div style={{ padding: 14, borderRadius: 14, background: "var(--purple-bg,rgba(124,58,237,0.08))", border: "1px solid rgba(124,58,237,0.2)", fontSize: 12, color: "var(--muted)", lineHeight: 1.7 }}>
              <span style={{ color: "var(--purple)", fontWeight: 700 }}>Omega ZI AI · </span>{accountInsight?.insight || "Loading insight..."}
            </div>
          </div>
        </div>
      )}

      {metricModal === "pnl" && (() => {
        const bd = dailyPnl?.breakdown || {};
        const periodTrades = dailyPnl?.periodTrades || [];
        const spark = dailyPnl?.sparkline || [];
        const sparkColor = pnlPos ? "#22c55e" : "#f87171";
        const PERIOD_LABELS = { today: "Today", week: "7 Days", month: "30 Days", quarter: "90 Days" };
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 1000, display: "grid", placeItems: "center" }} onClick={() => setMetricModal(null)}>
            <div style={{ width: "min(520px,94vw)", padding: 28, borderRadius: 24, background: "var(--surface)", border: "1px solid var(--border)", boxShadow: "0 30px 80px rgba(0,0,0,0.3)", maxHeight: "90vh", overflowY: "auto", color: "var(--text)" }} onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)" }}>P&amp;L Dashboard</div>
                <button onClick={() => setMetricModal(null)} style={{ background: "var(--layer2)", border: "1px solid var(--border)", color: "var(--text)", width: 32, height: 32, borderRadius: "50%", cursor: "pointer", fontSize: 16 }}>✕</button>
              </div>

              {/* Hero + sparkline */}
              <div style={{ display: "flex", alignItems: "flex-end", gap: 16, marginBottom: 20 }}>
                <div>
                  <div style={{ fontSize: 48, fontWeight: 800, letterSpacing: "-0.05em", color: pnlPos ? "var(--green)" : "var(--red)", lineHeight: 1 }}>{pnlPos ? "+" : ""}{fmt$(pnlVal)}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: pnlPos ? "var(--green)" : "var(--red)", marginTop: 4 }}>{pnlPos ? "+" : ""}{fmtN(pnlPct, 2)}% today</div>
                </div>
                {spark.length >= 2 && (
                  <div style={{ flex: 1, paddingBottom: 4 }}>
                    <EquitySparkline data={spark} color={sparkColor} width={180} height={48} />
                    <div style={{ fontSize: 8, color: "var(--dim)", marginTop: 3, textAlign: "right" }}>{spark.length} equity pts</div>
                  </div>
                )}
              </div>

              {/* Stats grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginBottom: 16 }}>
                {[
                  { label: "Equity", val: fmt$(dailyPnl?.totalEquity) },
                  { label: "Day Open", val: fmt$(dailyPnl?.dayOpenEquity) },
                  { label: "AP Positions", val: dailyPnl?.autopilotOpen ?? "—" },
                ].map(m => (
                  <div key={m.label} style={{ padding: "10px 12px", borderRadius: 12, background: "var(--layer2)", border: "1px solid var(--border)" }}>
                    <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 4 }}>{m.label}</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "var(--text)" }}>{m.val}</div>
                  </div>
                ))}
              </div>

              {/* Asset-class breakdown */}
              {Object.values(bd).some(v => v.count > 0) && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 8 }}>Today by Asset Class</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
                    {[["Options", bd.option], ["Equity", bd.equity], ["Crypto", bd.crypto]].map(([label, b]) => b && b.count > 0 ? (
                      <div key={label} style={{ padding: "10px 12px", borderRadius: 12, background: "var(--layer2)", border: "1px solid var(--border)" }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: "var(--muted)", marginBottom: 3 }}>{label} ({b.count})</div>
                        <div style={{ fontSize: 13, fontWeight: 800, color: (b.dayPnl || 0) >= 0 ? "var(--green)" : "var(--red)" }}>{(b.dayPnl || 0) >= 0 ? "+" : ""}{fmt$(b.dayPnl)}</div>
                        <div style={{ fontSize: 9, color: "var(--dim)" }}>{fmt$(b.value)} value</div>
                      </div>
                    ) : null)}
                  </div>
                </div>
              )}

              {/* Period selector */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 8 }}>Period Performance</div>
                <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                  {["today", "week", "month", "quarter"].map(p => (
                    <button key={p} onClick={() => setPnlPeriod(p)} style={{ fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 8, border: `1px solid ${pnlPeriod === p ? "rgba(96,165,250,0.5)" : "var(--border)"}`, background: pnlPeriod === p ? "rgba(96,165,250,0.12)" : "var(--layer2)", color: pnlPeriod === p ? "#60a5fa" : "var(--muted)", cursor: "pointer" }}>{PERIOD_LABELS[p]}</button>
                  ))}
                </div>
                {pnlPeriod !== "today" && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                    <div style={{ padding: "10px 12px", borderRadius: 12, background: "var(--layer2)", border: "1px solid var(--border)" }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: "var(--muted)", marginBottom: 3 }}>USD P&L</div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: (dailyPnl?.usdPnl || 0) >= 0 ? "var(--green)" : "var(--red)" }}>{(dailyPnl?.usdPnl || 0) >= 0 ? "+" : ""}{fmt$(dailyPnl?.usdPnl)}</div>
                      <div style={{ fontSize: 9, color: "var(--dim)" }}>{dailyPnl?.usdTrades || 0} real trades</div>
                    </div>
                    <div style={{ padding: "10px 12px", borderRadius: 12, background: "var(--layer2)", border: "1px solid var(--border)" }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: "var(--muted)", marginBottom: 3 }}>Zi$ P&L</div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: (dailyPnl?.ziPnl || 0) >= 0 ? "#a78bfa" : "#f87171" }}>{(dailyPnl?.ziPnl || 0) >= 0 ? "+" : ""}{fmt$(dailyPnl?.ziPnl)}</div>
                      <div style={{ fontSize: 9, color: "var(--dim)" }}>{dailyPnl?.ziTrades || 0} paper trades</div>
                    </div>
                  </div>
                )}
                {periodTrades.length > 0 && (
                  <div style={{ display: "grid", gap: 6, maxHeight: 200, overflowY: "auto" }}>
                    <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 2 }}>Trade History ({periodTrades.length})</div>
                    {periodTrades.map((t, i) => {
                      const tPnl = t.pnl || 0;
                      const tPos = tPnl >= 0;
                      const isZI = t.currency === "ZI";
                      return (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", borderRadius: 10, background: "var(--layer2)", border: "1px solid var(--border)" }}>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>{t.symbol || t.ticker || "—"}</div>
                            <div style={{ fontSize: 9, color: "var(--dim)" }}>{isZI ? "Zi$" : "USD"} · {t.closedAt ? new Date(t.closedAt).toLocaleDateString() : "—"}</div>
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 800, color: tPos ? "var(--green)" : "var(--red)", textAlign: "right" }}>
                            {tPos ? "+" : ""}{fmt$(tPnl)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Position breakdown */}
              {dailyPnl?.positions?.length > 0 && (
                <div style={{ display: "grid", gap: 6 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 2 }}>Open Positions Today</div>
                  {dailyPnl.positions.map((p, i) => {
                    const dp = p.dayPnl >= 0;
                    return (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 12px", borderRadius: 10, background: "var(--layer2)", border: "1px solid var(--border)" }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>{p.symbol}</div>
                          <div style={{ fontSize: 9, color: "var(--dim)" }}>{p.type} · total {(p.totalGainPct || 0) >= 0 ? "+" : ""}{fmtN(p.totalGainPct, 1)}%</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 13, fontWeight: 800, color: dp ? "var(--green)" : "var(--red)" }}>{dp ? "+" : ""}{fmt$(p.dayPnl)}</div>
                          <div style={{ fontSize: 9, color: dp ? "var(--green)" : "var(--red)" }}>{dp ? "+" : ""}{fmtN(p.dayPnlPct, 1)}%</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Brokerage transaction history */}
              {history.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--muted)", marginBottom: 8 }}>Brokerage Transactions</div>
                  <div style={{ display: "grid", gap: 5, maxHeight: 180, overflowY: "auto" }}>
                    {history.slice(0, 20).map((t, i) => {
                      const amt = parseFloat(t.amount || t.net_amount || 0);
                      const isPos = amt >= 0;
                      const desc = t.description || t.type || t.transaction_type || "Transaction";
                      const dateStr = t.date || t.created_at || t.timestamp || "";
                      return (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 10px", borderRadius: 8, background: "var(--layer2)", border: "1px solid var(--border)" }}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{String(desc).slice(0, 40)}</div>
                            <div style={{ fontSize: 9, color: "var(--dim)" }}>{dateStr ? new Date(dateStr).toLocaleDateString() : "—"}</div>
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 800, color: isPos ? "var(--green)" : "var(--red)", flexShrink: 0, marginLeft: 8 }}>{isPos ? "+" : ""}{fmt$(amt)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>
          </div>
        );
      })()}

      {detailPosition && <PositionDetailModal position={detailPosition} onClose={() => setDetailPosition(null)} />}
      {quickTrade && <QuickTradeModal position={quickTrade.position} mode={quickTrade.mode} onClose={() => setQuickTrade(null)} onSuccess={() => { setQuickTrade(null); loadAccountData(); refreshPortfolio?.(); }} />}
      {trailStop && <TrailStopModal position={trailStop.position} onClose={() => setTrailStop(null)} onSuccess={() => setTrailStop(null)} />}
    </div>
  );
}
